import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import gsap from 'gsap';
import { animate, stagger } from 'animejs';
import {
  AlertTriangle,
  BarChart3,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  FolderHeart,
  Heart,
  Home,
  Link,
  ListMusic,
  LogIn,
  Mic,
  MicOff,
  Moon,
  Music,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Repeat,
  Repeat1,
  Search,
  Settings,
  Share2,
  Shuffle,
  Users,
  SkipBack,
  SkipForward,
  Sun,
  Trash2,
  Type,
  UserCircle2,
  Volume2,
  VolumeX,
  Wifi,
  WifiOff,
  Zap
} from 'lucide-react';
import SearchResultsCard from './components/SearchResultsCard';
import { useTheme } from './context/ThemeContext';
import { API_BASE, EMPTY_PLAYER, sourceLabel, formatTime } from './lib';
import { isInDiscordActivity, setupDiscordActivity } from './discord-activity';
import type {
  HistoryItem,
  LibraryPayload,
  MembershipPayload,
  QueueItem,
  SearchItem,
  SearchPayload,
  SessionPayload,
  VoiceMembersPayload
} from './types';

type TabId = 'home' | 'search' | 'playlists' | 'favorites' | 'stats' | 'settings' | 'lyrics';

type DiscordRole = { id: string; name: string; color: number };

const TAB_ORDER: readonly TabId[] = ['home', 'search', 'playlists', 'favorites'];

const LAYOUT = {
  leftOffset: 16,
  rightOffset: 16,
  leftWidthExpanded: 240,
  leftWidthCollapsed: 64,
  rightWidth: 352,
  arrowGap: 14,
  sidebarTop: 20,
  sidebarBottom: 112,
  contentGap: 24,
  dockedRightInset: 24
} as const;

const CONSTANTS = {
  API_PATHS: {
    SESSION: '/auth/session',
    LOGIN: '/auth/discord/login',
    PLAYER: (guildId: string) => `/player/${encodeURIComponent(guildId)}`,
    QUEUE: (guildId: string) => `/queue/${encodeURIComponent(guildId)}`,
    MEMBERSHIP: (guildId: string) => `/bot/guilds/${encodeURIComponent(guildId)}/membership`,
    VOICE_MEMBERS: (guildId: string) => `/bot/guilds/${encodeURIComponent(guildId)}/voice-members`,
    LIBRARY: '/library',
    LIBRARY_FAVORITES_ADD: '/library/favorites/add',
    LIBRARY_FAVORITES_REMOVE: '/library/favorites/remove',
    LIBRARY_PLAYLISTS_CREATE: '/library/playlists/create',
    LIBRARY_PLAYLISTS_ADD: '/library/playlists/add',
    LIBRARY_PLAYLISTS_SHARE: '/library/playlists/share',
    LIBRARY_PLAYLISTS_UNSHARE: '/library/playlists/unshare',
    LIBRARY_PLAYLISTS_COLLABORATORS: '/library/playlists/collaborators',
    PLAYER_VOLUME: '/player/volume',
    EVENTS: (guildId: string) => `/events/${encodeURIComponent(guildId)}`,
    GUILD_ROLES: (guildId: string) => `/bot/guilds/${encodeURIComponent(guildId)}/roles`,
    GUILD_LYRICS: (guildId: string) => `/bot/guilds/${encodeURIComponent(guildId)}/lyrics`,
    GUILD_MEMBERS: (guildId: string) => `/bot/guilds/${encodeURIComponent(guildId)}/members`,
    GUILD_CHANNELS: (guildId: string) => `/bot/guilds/${encodeURIComponent(guildId)}/channels`,
    GUILD_SETTINGS: (guildId: string) => `/bot/guilds/${encodeURIComponent(guildId)}/settings`
  },
  STATUS: {
    CONNECTING: 'Connecting',
    LIVE: 'Live',
    OFFLINE: 'Offline',
    RECONNECTING: 'Reconnecting'
  },
  UI: {
    SEARCH_LIMIT: 10,
    GUILD_SCAN_LIMIT: 60,
    VOLUME_DEBOUNCE: 120
  }
} as const;

async function parseBody(res: Response) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return { error: text };
  }
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

async function api<T>(
  path: string,
  {
    method = 'GET',
    payload,
    csrfToken,
    activityToken,
    withCredentials = true
  }: {
    method?: string;
    payload?: Record<string, unknown>;
    csrfToken?: string | null;
    activityToken?: string | null;
    withCredentials?: boolean;
  } = {}
) {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (csrfToken && method !== 'GET') headers['x-csrf-token'] = csrfToken;
  if (activityToken) headers['x-activity-token'] = activityToken;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: withCredentials ? 'include' : 'same-origin',
    body: payload ? JSON.stringify(payload) : undefined
  });

  const body = await parseBody(res);
  if (!res.ok) {
    const message =
      typeof body.error === 'string'
        ? body.error
        : typeof body.message === 'string'
          ? body.message
          : typeof body.error === 'object' && body.error && 'message' in body.error && typeof body.error.message === 'string'
            ? body.error.message
            : `Request failed (${res.status})`;
    throw new Error(message);
  }

  if (body && typeof body === 'object' && 'data' in body) {
    return body.data as T;
  }

  return body as T;
}

function SkeletonBlock({ className }: { className: string }) {
  return <div className={`animate-pulse rounded-2xl bg-white/35 dark:bg-white/10 ${className}`} />;
}

export default function App() {
  const { theme, toggleTheme } = useTheme();
  const isActivityMode = useMemo(() => isInDiscordActivity(), []);
  const mode: 'activity' | 'dashboard' = isActivityMode ? 'activity' : 'dashboard';
  const [activityToken, setActivityToken] = useState<string | null>(null);
  const [activityError, setActivityError] = useState<string | null>(null);

  // === NAVIGATION STATE ===
  const [activeTab, setActiveTab] = useState<TabId>('home');
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightDocked, setRightDocked] = useState(false);
  const [serverPickerOpen, setServerPickerOpen] = useState(false);

  // === SESSION METADATA ===
  const [guildId, setGuildId] = useState('');
  const [guildName, setGuildName] = useState('');
  const [guilds, setGuilds] = useState<SessionPayload['guilds']>([]);
  const [onboardingGuildId, setOnboardingGuildId] = useState('');
  const [onboardingSearch, setOnboardingSearch] = useState('');

  // === AUTHENTICATION ===
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<SessionPayload['user']>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const defaultLoginUrl = `${API_BASE}${CONSTANTS.API_PATHS.LOGIN}?returnTo=${encodeURIComponent(window.location.href)}`;
  const [loginUrl, setLoginUrl] = useState(defaultLoginUrl);
  const [botInviteUrl, setBotInviteUrl] = useState<string | null>(null);

  // === CONTROL & PRESENCE ===
  const [membership, setMembership] = useState<MembershipPayload | null>(null);
  const [canControl, setCanControl] = useState(false);
  const [guildPresence, setGuildPresence] = useState<Record<string, MembershipPayload | null>>({});
  const [status, setStatus] = useState<string>(CONSTANTS.STATUS.CONNECTING);

  // === SEARCH & UI FEEDBACK ===
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSource, setSearchSource] = useState<'auto' | 'youtube' | 'soundcloud' | 'spotify'>('auto');
  const [loading, setLoading] = useState(false);
  const [guildLoading, setGuildLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const errorDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // === PLAYER DATA ===
  const [player, setPlayer] = useState(EMPTY_PLAYER);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [voice, setVoice] = useState<VoiceMembersPayload | null>(null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SearchItem[]>([]);
  const [library, setLibrary] = useState<LibraryPayload>({ favorites: [], playlists: [] });
  const [playlistName, setPlaylistName] = useState('');
  const [selectedPlaylistId, setSelectedPlaylistId] = useState('');

  // === HISTORY & DASHBOARD TABS ===
  const [history, setHistory] = useState<HistoryItem[]>(() => {
    try {
      const saved = localStorage.getItem('tkt-play-history');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [dashboardQueueTab, setDashboardQueueTab] = useState<'queue' | 'history'>('queue');

  // === QUICK PLAY ===
  const [quickPlayUrl, setQuickPlayUrl] = useState('');
  const [quickPlayLoading, setQuickPlayLoading] = useState(false);

  // === LYRICS ===
  const [lyrics, setLyrics] = useState<string | null>(null);
  const [lyricsLines, setLyricsLines] = useState<{ line: string; startMs: number }[]>([]);
  const [lyricsSynced, setLyricsSynced] = useState(false);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [showLyrics, setShowLyrics] = useState(false);
  const lastLyricsTrackRef = useRef<string | null>(null);
  const lyricsContainerRef = useRef<HTMLDivElement>(null);
  const [bgArtwork, setBgArtwork] = useState<string | null>(null);
  const bgFadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // === PLAYLIST SHARING ===
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [shareUserId, setShareUserId] = useState('');
  const [collaborators, setCollaborators] = useState<{ userId: string; accessLevel: string }[]>([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [serverMembers, setServerMembers] = useState<{ userId: string; username: string; displayName: string; avatarUrl: string | null }[]>([]);

  // === USER STATS ===
  const [userStats, setUserStats] = useState<{
    totalPlays: number; totalTimeMs: number; totalSearches: number; totalSkips: number;
    topTracks: { title: string; author: string; plays: number }[];
    topArtists: { author: string; plays: number }[];
    firstSeen: string | null;
  } | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // === AUDIO EFFECTS ===
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const EFFECT_PRESETS = [
    { id: 'bass-boost', label: 'Bass Boost' },
    { id: 'nightcore', label: 'Nightcore' },
    { id: 'vaporwave', label: 'Vaporwave' },
    { id: 'karaoke', label: 'Karaoke' },
    { id: '8d', label: '8D Audio' }
  ];

  // === TRACK REACTIONS ===
  const [reactions, setReactions] = useState<Record<string, { count: number; users: string[] }>>({});
  const REACTION_EMOJIS = ['🔥', '❤️', '💯', '👍', '🎉'];

  // === QUEUE DRAG (touch + mouse) ===
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragStartY = useRef<number>(0);
  const dragActive = useRef(false);

  // === PLAYLIST IMPORT ===
  const [importUrl, setImportUrl] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState('');
  const [importResult, setImportResult] = useState<string | null>(null);

  // === SETTINGS ===
  const [guildRoles, setGuildRoles] = useState<DiscordRole[]>([]);
  const [guildChannels, setGuildChannels] = useState<{ id: string; name: string }[]>([]);
  const [currentDjRoleId, setCurrentDjRoleId] = useState<string | null>(null);
  const [selectedDjRoleId, setSelectedDjRoleId] = useState<string | null>(null);
  const [nowPlayingChannelId, setNowPlayingChannelId] = useState<string | null>(null);
  const [selectedNowPlayingChannelId, setSelectedNowPlayingChannelId] = useState<string | null>(null);
  const [voteSkipEnabled, setVoteSkipEnabled] = useState(false);
  const [voteSkipThreshold, setVoteSkipThreshold] = useState(50);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  // Persist history to localStorage
  useEffect(() => {
    try { localStorage.setItem('tkt-play-history', JSON.stringify(history)); } catch { /* ignore */ }
  }, [history]);

  // Smooth album art background transition
  useEffect(() => {
    const url = player.nowPlaying?.artworkUrl ?? null;
    if (url) {
      if (bgFadeTimer.current) clearTimeout(bgFadeTimer.current);
      setBgArtwork(url);
    } else {
      // Delay clearing so the CSS fade-out has time to play
      bgFadeTimer.current = setTimeout(() => setBgArtwork(null), 3000);
    }
    return () => { if (bgFadeTimer.current) clearTimeout(bgFadeTimer.current); };
  }, [player.nowPlaying?.artworkUrl]);

  // Fetch lyrics when track changes
  useEffect(() => {
    const trackKey = player.nowPlaying?.title ?? null;
    if (trackKey === lastLyricsTrackRef.current) return;
    lastLyricsTrackRef.current = trackKey;
    if (!trackKey || !guildId) { setLyrics(null); setLyricsLines([]); setLyricsSynced(false); return; }
    setLyricsLoading(true);
    api<{ lyrics: { text: string; lines?: { line: string; startMs: number }[]; source?: string; synced?: boolean } | null }>(CONSTANTS.API_PATHS.GUILD_LYRICS(guildId), { activityToken })
      .then((data) => {
        setLyrics(data.lyrics?.text ?? null);
        setLyricsLines(data.lyrics?.lines ?? []);
        setLyricsSynced(data.lyrics?.synced ?? false);
      })
      .catch(() => { setLyrics(null); setLyricsLines([]); setLyricsSynced(false); })
      .finally(() => setLyricsLoading(false));
  }, [player.nowPlaying?.title, guildId]);

  // === LOCAL CONTROLS ===
  const [localVolume, setLocalVolume] = useState(EMPTY_PLAYER.volume);
  const volumeCommitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // === INTERPOLATED POSITION (ticks every second between server updates) ===
  const [displayPosition, setDisplayPosition] = useState(0);
  const lastServerPosition = useRef(0);
  const lastServerTime = useRef(Date.now());

  useEffect(() => {
    lastServerPosition.current = player.positionMs;
    lastServerTime.current = Date.now();
    setDisplayPosition(player.positionMs);
  }, [player.positionMs]);

  useEffect(() => {
    if (!player.nowPlaying || player.paused) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - lastServerTime.current;
      setDisplayPosition(lastServerPosition.current + elapsed);
    }, 1000);
    return () => clearInterval(interval);
  }, [player.nowPlaying, player.paused]);

  // Auto-scroll synced lyrics to active line
  useEffect(() => {
    if (activeTab !== 'lyrics' || !lyricsSynced || !lyricsLines.length || !lyricsContainerRef.current) return;
    const activeIdx = lyricsLines.findIndex((line, i) =>
      lyricsLines[i + 1]
        ? displayPosition >= line.startMs && displayPosition < lyricsLines[i + 1].startMs
        : displayPosition >= line.startMs
    );
    if (activeIdx < 0) return;
    const el = lyricsContainerRef.current.querySelector(`[data-lyrics-line="${activeIdx}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [displayPosition, activeTab, lyricsSynced, lyricsLines]);

  const tabs: Array<{ id: TabId; label: string; icon: typeof Home }> = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'search', label: 'Search', icon: Search },
    { id: 'playlists', label: 'Playlists', icon: ListMusic },
    { id: 'favorites', label: 'Favorites', icon: Heart },
    { id: 'stats', label: 'Stats', icon: BarChart3 }
  ];

  const appRef = useRef<HTMLDivElement>(null);
  const onboardingRef = useRef<HTMLDivElement>(null);
  const serverPickerRef = useRef<HTMLDivElement>(null);
  const serverPickerPanelRef = useRef<HTMLDivElement>(null);
  const activeTabPanelRef = useRef<HTMLElement | null>(null);
  const previousTabRef = useRef<TabId>('home');
  const introQueuedRef = useRef(false);
  const [showIntro, setShowIntro] = useState(false);
  const onboardingRequired = mode === 'dashboard' && sessionLoaded && !guildId.trim();

  useEffect(() => {
    if (!sessionLoaded || introQueuedRef.current) return;
    introQueuedRef.current = true;
    if (prefersReducedMotion()) return;
    setShowIntro(true);
  }, [sessionLoaded]);

  useLayoutEffect(() => {
    if (!showIntro) return;

    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        defaults: { ease: 'power2.out' },
        onComplete: () => setShowIntro(false)
      });

      timeline
        .fromTo(
          '.intro-logo-shell',
          { scale: 0.5, rotate: -16, opacity: 0 },
          { scale: 1, rotate: 0, opacity: 1, duration: 0.72, ease: 'back.out(1.5)' }
        )
        .fromTo(
          '.intro-ring',
          { scale: 0.4, opacity: 0 },
          { scale: 1.2, opacity: 0.65, duration: 0.72 },
          0.05
        )
        .fromTo(
          '.intro-title',
          { y: 18, letterSpacing: '0.16em', opacity: 0 },
          { y: 0, letterSpacing: '0.04em', opacity: 1, duration: 0.55 },
          0.2
        )
        .fromTo('.intro-subtitle', { y: 10, opacity: 0 }, { y: 0, opacity: 0.9, duration: 0.45 }, 0.3)
        .to('.intro-ring', { scale: 1.65, opacity: 0, duration: 0.5 }, '>-0.05')
        .to('.intro-overlay', { opacity: 0, duration: 0.45 }, '-=0.35');
    }, appRef);

    return () => ctx.revert();
  }, [showIntro]);

  useLayoutEffect(() => {
    if (!sessionLoaded || !introQueuedRef.current || showIntro || prefersReducedMotion()) return;

    const ctx = gsap.context(() => {
      gsap.fromTo('.tkt-logo', { y: -24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' });
      gsap.fromTo(
        '.dashboard-anim',
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.68, stagger: 0.1, ease: 'power2.out', delay: 0.12 }
      );
    }, appRef);

    return () => ctx.revert();
  }, [sessionLoaded, showIntro]);

  useLayoutEffect(() => {
    if (!sessionLoaded || !introQueuedRef.current || showIntro) return;

    if (prefersReducedMotion()) {
      previousTabRef.current = activeTab;
      return;
    }

    const panel = activeTabPanelRef.current;
    if (!panel) {
      previousTabRef.current = activeTab;
      return;
    }

    const previousIndex = TAB_ORDER.indexOf(previousTabRef.current);
    const nextIndex = TAB_ORDER.indexOf(activeTab);
    const direction = nextIndex >= previousIndex ? 1 : -1;
    const panelChildren = panel.querySelectorAll(':scope > *');

    const ctx = gsap.context(() => {
      gsap.fromTo(
        panel,
        { x: direction * 22, opacity: 0, filter: 'blur(6px)' },
        { x: 0, opacity: 1, filter: 'blur(0px)', duration: 0.34, ease: 'power2.out' }
      );
      gsap.fromTo(
        panelChildren,
        { y: direction * 10, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.28, stagger: 0.045, delay: 0.06, ease: 'power2.out' }
      );
    }, panel);

    previousTabRef.current = activeTab;

    return () => ctx.revert();
  }, [activeTab, sessionLoaded, showIntro]);

  useEffect(() => {
    if (!onboardingRequired || !onboardingRef.current || prefersReducedMotion()) return;

    const anim = animate(onboardingRef.current.querySelectorAll('.onboarding-item'), {
      translateY: [20, 0],
      opacity: [0, 1],
      delay: stagger(80),
      duration: 760,
      ease: 'outElastic(1, .8)'
    });

    return () => {
      if (anim) anim.pause();
    };
  }, [onboardingRequired, isAuthenticated, guilds.length]);

  useEffect(() => {
    if (!serverPickerOpen || !serverPickerPanelRef.current || prefersReducedMotion()) return;

    const pickerPanel = animate(serverPickerPanelRef.current, {
      translateY: [-8, 0],
      opacity: [0, 1],
      duration: 320,
      ease: 'outCubic'
    });

    const pickerItems = animate(serverPickerPanelRef.current.querySelectorAll('[data-picker-item]'), {
      translateX: [10, 0],
      opacity: [0, 1],
      delay: stagger(45),
      duration: 300,
      ease: 'outQuad'
    });

    return () => {
      if (pickerPanel) pickerPanel.pause();
      if (pickerItems) pickerItems.pause();
    };
  }, [serverPickerOpen, guilds.length, guildId]);

  // Close server picker on click outside
  useEffect(() => {
    if (!serverPickerOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (serverPickerRef.current && !serverPickerRef.current.contains(e.target as Node)) {
        setServerPickerOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [serverPickerOpen]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (!canControl) return;
      switch (e.key) {
        case ' ':
          e.preventDefault();
          control(player.paused ? '/player/resume' : '/player/pause');
          break;
        case 'ArrowRight':
          e.preventDefault();
          control('/player/skip');
          break;
        case 'ArrowUp':
          e.preventDefault();
          onVolumeInput(Math.min(200, player.volume + 10));
          break;
        case 'ArrowDown':
          e.preventDefault();
          onVolumeInput(Math.max(0, player.volume - 10));
          break;
        case '/':
          e.preventDefault();
          selectTab('search', null);
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  function selectTab(tabId: TabId, trigger: HTMLElement | null) {
    if (tabId === activeTab) return;

    if (trigger && !prefersReducedMotion()) {
      animate(trigger, {
        scale: [1, 0.94, 1],
        duration: 240,
        ease: 'outQuad'
      });
    }

    setActiveTab(tabId);
  }

  const currentLeftWidth = leftCollapsed ? LAYOUT.leftWidthCollapsed : LAYOUT.leftWidthExpanded;
  const desktopLeftInset = LAYOUT.leftOffset + currentLeftWidth + LAYOUT.contentGap;
  const desktopRightInset = rightDocked
    ? LAYOUT.dockedRightInset
    : LAYOUT.rightOffset + LAYOUT.rightWidth + LAYOUT.contentGap;

  const mainLayoutStyle = {
    '--main-left': `${desktopLeftInset}px`,
    '--main-right': `${desktopRightInset}px`
  } as CSSProperties;

  const leftSidebarStyle = {
    left: `${LAYOUT.leftOffset}px`,
    width: `${currentLeftWidth}px`
  } as CSSProperties;

  const rightSidebarStyle = {
    right: `${LAYOUT.rightOffset}px`,
    width: `${LAYOUT.rightWidth}px`
  } as CSSProperties;

  const arrowTopPosition = `calc(${LAYOUT.sidebarTop}px + (100vh - ${LAYOUT.sidebarTop}px - ${LAYOUT.sidebarBottom}px) / 2)`;
  const leftArrowStyle = {
    top: arrowTopPosition,
    left: `${LAYOUT.leftOffset + currentLeftWidth - 18}px`
  } as CSSProperties;

  const rightArrowUndockStyle = {
    top: arrowTopPosition,
    right: `${LAYOUT.dockedRightInset - 18}px`
  } as CSSProperties;

  const rightArrowDockStyle = {
    top: '50%',
    left: '-1.125rem'
  } as CSSProperties;

  const filteredGuilds = useMemo(() => {
    const needle = onboardingSearch.trim().toLowerCase();
    return [...guilds]
      .filter((guild) => guild.name.toLowerCase().includes(needle) && guildPresence[guild.id]?.joined === true)
      .sort((a, b) => Number(Boolean(b.canManage || b.canDJ)) - Number(Boolean(a.canManage || a.canDJ)) || a.name.localeCompare(b.name));
  }, [guilds, onboardingSearch, guildPresence]);
  const guildScanInProgress = onboardingRequired && isAuthenticated && guilds.length > 0 && guilds.some((guild) => guildPresence[guild.id] === undefined);

  const selectedGuild = guilds.find((guild) => guild.id === onboardingGuildId) || null;
  const activeGuild = guilds.find((guild) => guild.id === guildId) || null;
  const activeGuildIcon = activeGuild?.icon
    ? `https://cdn.discordapp.com/icons/${activeGuild.id}/${activeGuild.icon}.png?size=64`
    : null;

  /** Persist guild selection to state and localStorage */
  function persistGuild(nextId: string, nextName: string) {
    setGuildId(nextId);
    setGuildName(nextName || nextId);
    localStorage.setItem('tkt-selected-guild-id', nextId);
    localStorage.setItem('tkt-selected-guild-name', nextName || nextId);
  }

  const fetchPlayerSnapshot = (nextGuildId: string) =>
    api<typeof EMPTY_PLAYER>(CONSTANTS.API_PATHS.PLAYER(nextGuildId), { activityToken });

  const fetchQueueSnapshot = (nextGuildId: string) =>
    api<{ items: QueueItem[] }>(CONSTANTS.API_PATHS.QUEUE(nextGuildId), { activityToken });

  const fetchMembershipPayload = (nextGuildId: string) =>
    api<MembershipPayload>(CONSTANTS.API_PATHS.MEMBERSHIP(nextGuildId), { activityToken });

  const fetchVoiceMembersPayload = (nextGuildId: string) =>
    api<VoiceMembersPayload>(CONSTANTS.API_PATHS.VOICE_MEMBERS(nextGuildId), { activityToken });

  const fetchLibraryPayload = () =>
    api<LibraryPayload>(CONSTANTS.API_PATHS.LIBRARY, {
      activityToken,
      withCredentials: !isActivityMode
    });

  const fetchSessionPayload = () => api<SessionPayload>(CONSTANTS.API_PATHS.SESSION);

  function showError(error: unknown, fallback = 'Something went wrong') {
    const message = error instanceof Error && error.message ? error.message : fallback;
    setErrorMessage(message);
    if (errorDismissTimer.current) {
      clearTimeout(errorDismissTimer.current);
    }
    errorDismissTimer.current = setTimeout(() => {
      setErrorMessage(null);
      errorDismissTimer.current = null;
    }, 6500);
  }

  /** Refresh player + queue snapshots for the active guild */
  async function refreshSnapshot(nextGuildId: string) {
    const [nextPlayer, nextQueue] = await Promise.all([
      fetchPlayerSnapshot(nextGuildId),
      fetchQueueSnapshot(nextGuildId)
    ]);

    setPlayer(nextPlayer || EMPTY_PLAYER);
    setQueue(nextQueue.items || []);
  }

  /** Refresh membership data and permissions */
  async function refreshMembership(nextGuildId: string) {
    const result = await fetchMembershipPayload(nextGuildId);
    setMembership(result);
    setCanControl(Boolean(result.joined));
  }

  /** Refresh voice participants */
  async function refreshVoiceMembers(nextGuildId: string) {
    const result = await fetchVoiceMembersPayload(nextGuildId);
    setVoice(result);
  }

  /** Refresh the library payload and sync selected playlist */
  async function refreshLibrary() {
    const result = await fetchLibraryPayload();
    setLibrary(result);
    if (!selectedPlaylistId && result.playlists.length > 0) {
      setSelectedPlaylistId(result.playlists[0].id);
    }
  }

  /** Initialize session data (activity or dashboard) */
  async function initializeSession() {
    const savedGuildId = localStorage.getItem('tkt-selected-guild-id') || '';
    const savedGuildName = localStorage.getItem('tkt-selected-guild-name') || '';

    if (isActivityMode) {
      try {
        const identity = await setupDiscordActivity();
        setActivityToken(identity.activityToken);
        setIsAuthenticated(true);
        setCurrentUser({
          id: identity.user.id,
          username: identity.user.username,
          avatar: identity.user.avatar
        });
        setGuilds([{ id: identity.guildId, name: identity.guildId }]);
        persistGuild(identity.guildId, identity.guildId);
        setOnboardingGuildId(identity.guildId);
      } catch (err) {
        console.error('Discord activity setup failed', err);
        setActivityError(err instanceof Error ? err.message : 'Discord activity setup failed');
      }
      setSessionLoaded(true);
      return;
    }

    // Hydrate from cached session to avoid Discord login redirect on every visit
    const SESSION_CACHE_KEY = 'tkt-session-cache';
    const SESSION_CACHE_MAX_AGE = 1000 * 60 * 60; // 1 hour
    let usedCache = false;

    try {
      const cached = localStorage.getItem(SESSION_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached) as SessionPayload & { cachedAt: number };
        if (parsed.cachedAt && Date.now() - parsed.cachedAt < SESSION_CACHE_MAX_AGE && parsed.authenticated) {
          setCsrfToken(parsed.csrfToken);
          setIsAuthenticated(true);
          setCurrentUser(parsed.user);
          setGuilds(parsed.guilds || []);
          const matched = parsed.guilds?.find((guild) => guild.id === savedGuildId);
          if (matched) {
            persistGuild(matched.id, matched.name);
            setOnboardingGuildId(matched.id);
          } else if (parsed.guilds && parsed.guilds.length > 0) {
            setOnboardingGuildId(parsed.guilds[0].id);
            setGuildName(savedGuildName || '');
          }
          setSessionLoaded(true);
          usedCache = true;
        }
      }
    } catch {
      // ignore cache read errors
    }

    // Fetch fresh session (foreground if no cache, background if cached)
    const applySession = (session: SessionPayload) => {
      setCsrfToken(session.csrfToken);
      setLoginUrl(`${API_BASE}${CONSTANTS.API_PATHS.LOGIN}?returnTo=${encodeURIComponent(window.location.href)}`);
      setIsAuthenticated(Boolean(session.authenticated));
      setCurrentUser(session.user);
      setGuilds(session.guilds || []);
      if (session.botInviteUrl) setBotInviteUrl(session.botInviteUrl);

      if (session.authenticated) {
        try {
          localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify({ ...session, cachedAt: Date.now() }));
        } catch { /* ignore */ }
        const matched = session.guilds?.find((guild) => guild.id === savedGuildId);
        if (matched) {
          persistGuild(matched.id, matched.name);
          setOnboardingGuildId(matched.id);
        } else if (session.guilds && session.guilds.length > 0) {
          setOnboardingGuildId(session.guilds[0].id);
          setGuildName(savedGuildName || '');
        }
      } else {
        localStorage.removeItem(SESSION_CACHE_KEY);
        setOnboardingGuildId(savedGuildId || '');
        setGuildName(savedGuildName || '');
      }
    };

    const handleSessionError = () => {
      localStorage.removeItem(SESSION_CACHE_KEY);
      setLoginUrl(`${API_BASE}${CONSTANTS.API_PATHS.LOGIN}?returnTo=${encodeURIComponent(window.location.href)}`);
      setIsAuthenticated(false);
      setCurrentUser(null);
      setGuilds([]);
      setOnboardingGuildId(savedGuildId || '');
      setGuildName(savedGuildName || '');
    };

    if (usedCache) {
      // Refresh in background — don't block rendering
      fetchSessionPayload().then(applySession).catch((err) => {
        console.warn('Background session refresh failed', err);
        handleSessionError();
        setSessionLoaded(true);
      });
    } else {
      try {
        const session = await fetchSessionPayload();
        applySession(session);
      } catch (err) {
        console.warn('Session fetch failed or unauthorized', err);
        handleSessionError();
      }
      setSessionLoaded(true);
    }
  }

  /** Send control actions (play/pause/skip, etc.) */
  async function control(path: string, payload: Record<string, unknown> = {}) {
    const targetGuild = guildId.trim();
    if (!targetGuild || !canControl) return;

    setLoading(true);
    try {
      await api(path, {
        method: 'POST',
        payload: { guildId: targetGuild, ...payload },
        csrfToken,
        activityToken
      });
      await refreshSnapshot(targetGuild);
    } catch (err) {
      showError(err, 'Control action failed');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        await initializeSession();
      } catch (err) {
        setStatus(CONSTANTS.STATUS.OFFLINE);
        setSessionLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!onboardingRequired || !isAuthenticated || guilds.length === 0) return;

    let cancelled = false;
    const targets = guilds.slice(0, CONSTANTS.UI.GUILD_SCAN_LIMIT);

    (async () => {
      const entries = await Promise.all(
        targets.map(async (guild) => {
          try {
            const payload = await fetchMembershipPayload(guild.id);
            return [guild.id, payload] as const;
          } catch {
            return [guild.id, null] as const;
          }
        })
      );

      if (cancelled) return;
      setGuildPresence((prev) => ({
        ...prev,
        ...Object.fromEntries(entries)
      }));
    })();

    return () => {
      cancelled = true;
    };
  }, [onboardingRequired, isAuthenticated, guilds, activityToken]);

  useEffect(() => {
    let source: EventSource | null = null;
    const gid = guildId.trim();
    if (!gid) return;

    setGuildLoading(true);
    (async () => {
      try {
        await refreshMembership(gid);
        await Promise.all([refreshSnapshot(gid), refreshVoiceMembers(gid), refreshLibrary()]);
        setStatus(CONSTANTS.STATUS.LIVE);
      } catch {
        setStatus(CONSTANTS.STATUS.OFFLINE);
      } finally {
        setGuildLoading(false);
      }
    })();

    const query = isActivityMode && activityToken ? `?activityToken=${encodeURIComponent(activityToken)}` : '';
    source = new EventSource(`${API_BASE}${CONSTANTS.API_PATHS.EVENTS(gid)}${query}`, { withCredentials: !isActivityMode });
    let voiceRefreshTimer: ReturnType<typeof setTimeout> | null = null;
    source.addEventListener('snapshot', (event) => {
      const payload = JSON.parse(event.data) as { player: typeof EMPTY_PLAYER; queue: QueueItem[]; reason?: string; reactions?: Record<string, { count: number; users: string[] }> };
      setPlayer(payload.player || EMPTY_PLAYER);
      setQueue(payload.queue || []);
      if (payload.reactions) {
        // Trigger floating emojis for new reactions
        const reason = payload.reason || '';
        if (reason === 'player_react') {
          const bar = document.querySelector('nav.fixed');
          if (bar) {
            const barRect = bar.getBoundingClientRect();
            for (const [em, data] of Object.entries(payload.reactions)) {
              const prev = reactions[em];
              if (!prev || data.count > prev.count) {
                const randomX = barRect.left + Math.random() * barRect.width * 0.6 + barRect.width * 0.2;
                const el = document.createElement('div');
                el.className = 'emoji-float';
                el.textContent = em;
                el.style.left = `${randomX - 16}px`;
                el.style.bottom = `${window.innerHeight - barRect.top + 8}px`;
                document.body.appendChild(el);
                setTimeout(() => el.remove(), 2000);
              }
            }
          }
        }
        setReactions(payload.reactions);
      }
      setStatus(CONSTANTS.STATUS.LIVE);

      // Throttled voice member refresh on join/leave/update events
      const reason = payload.reason || '';
      if (reason.includes('join') || reason.includes('leave') || reason === 'player_play' || reason === 'player_stop') {
        if (voiceRefreshTimer) clearTimeout(voiceRefreshTimer);
        voiceRefreshTimer = setTimeout(() => refreshVoiceMembers(gid), 1500);
      }
    });
    source.onerror = () => setStatus(CONSTANTS.STATUS.RECONNECTING);

    return () => {
      if (source) source.close();
      if (voiceRefreshTimer) clearTimeout(voiceRefreshTimer);
    };
  }, [guildId, isActivityMode, activityToken]);

  useEffect(() => {
    setLocalVolume(player.volume);
  }, [player.volume]);

  useEffect(() => () => {
    if (volumeCommitTimer.current) {
      clearTimeout(volumeCommitTimer.current);
      volumeCommitTimer.current = null;
    }
    if (errorDismissTimer.current) {
      clearTimeout(errorDismissTimer.current);
      errorDismissTimer.current = null;
    }
  }, []);

  // === HISTORY TRACKING ===
  useEffect(() => {
    if (player.nowPlaying?.title) {
      setHistory((prev) => {
        // If the same track is already at the top, increment its play count
        if (prev.length > 0 && prev[0].title === player.nowPlaying!.title) {
          const updated = [...prev];
          updated[0] = { ...updated[0], playCount: (updated[0].playCount || 1) + 1, playedAt: new Date().toISOString() };
          return updated;
        }
        const newItem: HistoryItem = {
          id: `${Date.now()}`,
          title: player.nowPlaying!.title,
          url: player.nowPlaying?.url,
          source: player.nowPlaying?.source,
          requestedBy: player.nowPlaying?.requestedBy,
          playedAt: new Date().toISOString(),
          playCount: 1
        };
        return [newItem, ...prev].slice(0, 50);
      });
    }
  }, [player.nowPlaying?.title]);

  /** Pushes the committed volume to the server */
  async function commitVolume(volume: number) {
    const targetGuild = guildId.trim();
    if (!targetGuild || !canControl) return;

    try {
      await api(CONSTANTS.API_PATHS.PLAYER_VOLUME, {
        method: 'POST',
        payload: { guildId: targetGuild, volume },
        csrfToken,
        activityToken
      });
    } catch (err) {
      showError(err, 'Volume update failed');
      void refreshSnapshot(targetGuild);
    }
  }

  /** Debounces volume updates */
  function queueVolumeCommit(volume: number) {
    if (volumeCommitTimer.current) {
      clearTimeout(volumeCommitTimer.current);
    }
    volumeCommitTimer.current = setTimeout(() => {
      void commitVolume(volume);
      volumeCommitTimer.current = null;
    }, CONSTANTS.UI.VOLUME_DEBOUNCE);
  }

  /** Handles the local slider and volume updates */
  function onVolumeInput(volume: number) {
    const nextVolume = Math.max(0, Math.min(200, Math.round(volume)));
    setLocalVolume(nextVolume);
    setPlayer((prev) => ({ ...prev, volume: nextVolume }));
    queueVolumeCommit(nextVolume);
  }

  const searchPath = (query: string) =>
    `/search?query=${encodeURIComponent(query)}&sourceMode=${searchSource}&limit=${CONSTANTS.UI.SEARCH_LIMIT}`;

  /** Execute a search against the API */
  async function searchTracks() {
    const text = searchQuery.trim();
    if (!text) return;

    setLoading(true);
    try {
      const results = await api<SearchPayload>(searchPath(text), {
        activityToken,
        withCredentials: !isActivityMode
      });
      setSearchResults(results.items || []);
    } catch (err) {
      showError(err, 'Search failed');
    } finally {
      setLoading(false);
    }
  }

  /** Queue a track for playback */
  async function queueTrack(track: SearchItem) {
    await control('/player/play', {
      title: track.title,
      url: track.url || null,
      source: track.source || null,
      author: track.author || null,
      durationMs: track.durationMs || 0,
      requestedBy: isActivityMode ? 'discord-activity' : 'dashboard-ui'
    });
  }

  /** Play a URL directly (YouTube, Spotify, etc.) */
  async function playUrl(url: string) {
    const trimmed = url.trim();
    if (!trimmed) return;
    setQuickPlayLoading(true);
    try {
      await control('/player/play', {
        title: trimmed,
        url: trimmed,
        query: trimmed,
        source: null,
        requestedBy: isActivityMode ? 'discord-activity' : 'dashboard-ui'
      });
      setQuickPlayUrl('');
    } catch (err) {
      showError(err, 'Unable to start playback');
    } finally {
      setQuickPlayLoading(false);
    }
  }

  /** Replace the current queue with a track */
  async function playNow(track: SearchItem) {
    await control('/player/stop');
    await queueTrack(track);
  }

  const postLibraryAction = async (path: string, track: SearchItem) =>
    api(path, {
      method: 'POST',
      payload: { track },
      csrfToken,
      activityToken,
      withCredentials: !isActivityMode
    });

  /** Add a favorite track */
  async function addFavorite(track: SearchItem) {
    await postLibraryAction(CONSTANTS.API_PATHS.LIBRARY_FAVORITES_ADD, track);
    await refreshLibrary();
  }

  /** Remove a favorite track */
  async function removeFavorite(track: SearchItem) {
    await postLibraryAction(CONSTANTS.API_PATHS.LIBRARY_FAVORITES_REMOVE, track);
    await refreshLibrary();
  }

  /** Create a new playlist */
  async function createPlaylist() {
    const name = playlistName.trim();
    if (!name) return;
    await api(CONSTANTS.API_PATHS.LIBRARY_PLAYLISTS_CREATE, {
      method: 'POST',
      payload: { name },
      csrfToken,
      activityToken,
      withCredentials: !isActivityMode
    });
    setPlaylistName('');
    const refreshed = await fetchLibraryPayload();
    setLibrary(refreshed);
    // Auto-select the newly created playlist
    const newest = refreshed.playlists.find((p) => p.name === name);
    if (newest) setSelectedPlaylistId(newest.id);
  }

  /** Add a track to the selected playlist */
  async function addToPlaylist(track: SearchItem) {
    if (!selectedPlaylistId) return;
    await api(CONSTANTS.API_PATHS.LIBRARY_PLAYLISTS_ADD, {
      method: 'POST',
      payload: { playlistId: selectedPlaylistId, track },
      csrfToken,
      activityToken,
      withCredentials: !isActivityMode
    });
    await refreshLibrary();
  }

  /** Load guild settings (roles list + current DJ role) */
  async function loadGuildSettings(targetGuildId: string) {
    setSettingsLoading(true);
    setSettingsSaved(false);
    try {
      const [roles, channels, settings] = await Promise.all([
        api<DiscordRole[]>(CONSTANTS.API_PATHS.GUILD_ROLES(targetGuildId), { activityToken }),
        api<{ id: string; name: string }[]>(CONSTANTS.API_PATHS.GUILD_CHANNELS(targetGuildId), { activityToken }),
        api<{ djRoleId: string | null; nowPlayingChannelId: string | null; voteSkipEnabled: boolean; voteSkipThreshold: number }>(CONSTANTS.API_PATHS.GUILD_SETTINGS(targetGuildId), { activityToken })
      ]);
      setGuildRoles(roles || []);
      setGuildChannels(channels || []);
      setCurrentDjRoleId(settings.djRoleId);
      setSelectedDjRoleId(settings.djRoleId);
      setNowPlayingChannelId(settings.nowPlayingChannelId);
      setSelectedNowPlayingChannelId(settings.nowPlayingChannelId);
      setVoteSkipEnabled(settings.voteSkipEnabled ?? false);
      setVoteSkipThreshold(settings.voteSkipThreshold ?? 50);
    } catch (err) {
      console.warn('Failed to load settings', err);
    } finally {
      setSettingsLoading(false);
    }
  }

  /** Save all guild settings */
  async function saveSettings() {
    if (!guildId) return;
    setSettingsLoading(true);
    setSettingsSaved(false);
    try {
      await api(CONSTANTS.API_PATHS.GUILD_SETTINGS(guildId), {
        method: 'POST',
        payload: {
          djRoleId: selectedDjRoleId || null,
          nowPlayingChannelId: selectedNowPlayingChannelId || null,
          voteSkipEnabled,
          voteSkipThreshold
        },
        csrfToken,
        activityToken
      });
      setCurrentDjRoleId(selectedDjRoleId);
      setNowPlayingChannelId(selectedNowPlayingChannelId);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (err) {
      console.warn('Failed to save settings', err);
    } finally {
      setSettingsLoading(false);
    }
  }

  // Auto-load stats when stats tab is opened
  useEffect(() => {
    if (activeTab === 'stats' && !userStats && !statsLoading) loadStats();
  }, [activeTab]);

  /** Track a user event for analytics */
  function trackEvent(eventType: string, eventData: Record<string, unknown> = {}) {
    api('/library/track-event', {
      method: 'POST',
      payload: { guildId, eventType, eventData },
      csrfToken, activityToken, withCredentials: !isActivityMode
    }).catch(() => {});
  }

  /** Load user listening stats */
  async function loadStats() {
    setStatsLoading(true);
    try {
      const stats = await api<typeof userStats>('/library/stats', { activityToken, withCredentials: !isActivityMode });
      setUserStats(stats);
    } catch { setUserStats(null); }
    finally { setStatsLoading(false); }
  }

  /** Load server members for sharing */
  async function loadServerMembers() {
    if (!guildId) return;
    try {
      const members = await api<{ userId: string; username: string; displayName: string; avatarUrl: string | null }[]>(
        CONSTANTS.API_PATHS.GUILD_MEMBERS(guildId), { activityToken }
      );
      setServerMembers(members || []);
    } catch { setServerMembers([]); }
  }

  /** Load collaborators for the selected playlist */
  async function loadCollaborators(playlistId: string) {
    try {
      const data = await api<{ collaborators: { userId: string; accessLevel: string }[] }>(
        `${CONSTANTS.API_PATHS.LIBRARY_PLAYLISTS_COLLABORATORS}?playlistId=${encodeURIComponent(playlistId)}`,
        { csrfToken, activityToken, withCredentials: !isActivityMode }
      );
      setCollaborators(data.collaborators || []);
    } catch {
      setCollaborators([]);
    }
  }

  /** Share playlist with a user */
  async function sharePlaylist(targetUserId: string) {
    if (!selectedPlaylistId || !targetUserId.trim()) return;
    setShareLoading(true);
    try {
      await api(CONSTANTS.API_PATHS.LIBRARY_PLAYLISTS_SHARE, {
        method: 'POST',
        payload: { playlistId: selectedPlaylistId, targetUserId: targetUserId.trim() },
        csrfToken, activityToken, withCredentials: !isActivityMode
      });
      setShareUserId('');
      await loadCollaborators(selectedPlaylistId);
    } catch (err) {
      console.warn('Share failed', err);
    } finally {
      setShareLoading(false);
    }
  }

  /** Remove a collaborator */
  async function unsharePlaylist(targetUserId: string) {
    if (!selectedPlaylistId) return;
    try {
      await api(CONSTANTS.API_PATHS.LIBRARY_PLAYLISTS_UNSHARE, {
        method: 'POST',
        payload: { playlistId: selectedPlaylistId, targetUserId },
        csrfToken, activityToken, withCredentials: !isActivityMode
      });
      await loadCollaborators(selectedPlaylistId);
    } catch (err) {
      console.warn('Unshare failed', err);
    }
  }

  /** Import a YouTube/Spotify playlist into the selected user playlist */
  async function importPlaylist() {
    const url = importUrl.trim();
    if (!url || !selectedPlaylistId) return;
    setImportLoading(true);
    setImportResult(null);
    setImportProgress('Fetching playlist...');
    try {
      const results = await api<SearchPayload>(
        `/search?query=${encodeURIComponent(url)}&sourceMode=auto&limit=200`,
        { activityToken, withCredentials: !isActivityMode }
      );
      const tracks = results.items || [];
      if (tracks.length === 0) {
        setImportResult('No tracks found at that URL.');
        return;
      }
      for (let i = 0; i < tracks.length; i++) {
        setImportProgress(`Adding track ${i + 1} of ${tracks.length}...`);
        await api(CONSTANTS.API_PATHS.LIBRARY_PLAYLISTS_ADD, {
          method: 'POST',
          payload: { playlistId: selectedPlaylistId, track: tracks[i] },
          csrfToken,
          activityToken,
          withCredentials: !isActivityMode
        });
      }
      await refreshLibrary();
      setImportUrl('');
      setImportResult(`Imported ${tracks.length} track${tracks.length === 1 ? '' : 's'} into "${selectedPlaylist?.name}"`);
      setTimeout(() => setImportResult(null), 4000);
    } catch (err) {
      setImportResult(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImportLoading(false);
      setImportProgress('');
    }
  }

  /** Store the selected guild and unlock dashboard */
  function applyGuildSelection() {
    const raw = onboardingGuildId.trim();
    if (!raw) return;

    const known = guilds.find((guild) => guild.id === raw);
    const resolvedName = known ? known.name : raw;
    if (!known) {
      setGuilds((prev) => [...prev, { id: raw, name: resolvedName }]);
    }
    persistGuild(raw, resolvedName);
  }

  // Auto-select first playlist if none selected
  useEffect(() => {
    if (!selectedPlaylistId && library.playlists.length > 0) {
      setSelectedPlaylistId(library.playlists[0].id);
    }
  }, [library.playlists, selectedPlaylistId]);

  const selectedPlaylist = library.playlists.find((playlist) => playlist.id === selectedPlaylistId) || null;
  const canContinueWithGuild = Boolean(onboardingGuildId) && Boolean(selectedGuild?.canManage || selectedGuild?.canDJ);

  return (
    <div ref={appRef} className="h-screen bg-gradient-to-br from-fuchsia-50 via-violet-50 to-cyan-50 dark:from-[#0a0514] dark:via-[#120822] dark:to-[#0d0a1a] relative overflow-hidden">
      {/* Dynamic album art background */}
      <div className="album-art-bg" style={{ opacity: player.nowPlaying?.artworkUrl ? 1 : 0 }}>
        {bgArtwork && <img src={bgArtwork} alt="" />}
      </div>

      {showIntro && (
        <div className="intro-overlay fixed inset-0 z-[70] flex items-center justify-center flex-col gap-3 bg-slate-950/80 backdrop-blur-md pointer-events-none">
          <div className="intro-ring absolute h-56 w-56 rounded-full border border-cyan-300/60 shadow-[0_0_80px_rgba(34,211,238,0.28)]" />
          <div className="intro-logo-shell relative h-20 w-20 rounded-3xl bg-gradient-to-br from-pink-500 via-fuchsia-500 to-cyan-400 p-[3px]">
            <div className="h-full w-full rounded-[1.35rem] bg-slate-900/90 flex items-center justify-center">
              <img src="/logo.png" alt="TKT logo" className="h-12 w-12 rounded-xl" />
            </div>
          </div>
          <div className="intro-title text-xl font-bold tracking-[0.04em] text-white">TKT Music</div>
          <div className="intro-subtitle text-xs uppercase tracking-[0.18em] text-cyan-200/90">Loading control deck</div>
        </div>
      )}

      {errorMessage && (
        <div className="fixed top-4 left-4 right-4 sm:left-auto sm:right-5 sm:w-[360px] z-[80] y2k-card rounded-2xl p-3 shadow-2xl border border-red-300/60 dark:border-red-500/40">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-xl bg-red-500/15 p-2 text-red-500">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-800 dark:text-white">Action failed</div>
              <div className="text-xs text-gray-600 dark:text-gray-300 break-words">{errorMessage}</div>
            </div>
            <button
              type="button"
              onClick={() => setErrorMessage(null)}
              className="rounded-lg px-2 py-1 text-xs text-gray-500 hover:bg-white/40 dark:hover:bg-white/10"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* === BACKGROUND BLUR === */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[400px] lg:w-[500px] h-[400px] lg:h-[500px] bg-gradient-to-br from-pink-300 to-fuchsia-400 dark:from-pink-600 dark:to-fuchsia-700 opacity-35 dark:opacity-20 blob animate-morph" style={{ animationDelay: '0s' }} />
        <div className="absolute top-1/3 -right-32 w-[350px] lg:w-[400px] h-[350px] lg:h-[400px] bg-gradient-to-br from-lime-200 to-green-300 dark:from-lime-600 dark:to-green-700 opacity-30 dark:opacity-20 blob animate-morph animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute -bottom-32 left-1/4 w-[400px] lg:w-[450px] h-[400px] lg:h-[450px] bg-gradient-to-br from-cyan-200 to-blue-300 dark:from-cyan-600 dark:to-blue-700 opacity-30 dark:opacity-20 blob animate-morph" style={{ animationDelay: '4s' }} />
      </div>

      {/* === LEFT SIDEBAR === */}
      <aside className="hidden lg:flex fixed top-5 bottom-28 z-30 y2k-card rounded-3xl p-3 flex-col justify-between transition-all" style={leftSidebarStyle}>
        {/* Collapse toggle - vertically centered on right edge */}
        <button
          onClick={() => setLeftCollapsed((v) => !v)}
          className="absolute z-10 -right-3 top-1/2 -translate-y-1/2 w-6 h-10 rounded-r-xl bg-white/70 dark:bg-purple-950/70 border border-l-0 border-white/30 dark:border-purple-700/30 flex items-center justify-center hover:bg-white/90 dark:hover:bg-purple-900/70 transition-all"
        >
          {leftCollapsed ? <ChevronRight className="w-3 h-3 text-gray-500 dark:text-gray-400" /> : <ChevronLeft className="w-3 h-3 text-gray-500 dark:text-gray-400" />}
        </button>
        <div className="space-y-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = tab.id === activeTab;
            const iconSizeClass = leftCollapsed ? 'w-6 h-6' : 'w-5 h-5';
            const iconStrokeWidth = leftCollapsed ? 2.5 : 2.0;
            const iconDelay = `${tabs.indexOf(tab) * 0.5}s`;
            const iconElement = (
              <Icon
                className={`animate-subtle-float ${iconSizeClass}`}
                style={{ animationDelay: iconDelay }}
                strokeWidth={iconStrokeWidth}
              />
            );

            return (
              <button
                key={tab.id}
                className={`w-full rounded-2xl flex items-center transition-all duration-300 ease-out ripple-effect hover-scale ${
                  active ? 'btn-gradient animate-glow-pulse' : 'bubble-btn'
                } ${leftCollapsed ? 'justify-center p-2.5' : 'justify-start px-3 py-2.5 gap-3 text-sm'}`}
                onClick={(event) => selectTab(tab.id, event.currentTarget)}
                title={tab.label}
              >
                {iconElement}
                {!leftCollapsed && <span className="whitespace-nowrap overflow-hidden transition-all duration-300">{tab.label}</span>}
              </button>
            );
          })}
        </div>
        {/* Theme toggle + Settings pinned to bottom */}
        <div className="space-y-2">
          <button
            onClick={toggleTheme}
            className={`w-full rounded-2xl flex items-center transition-all duration-300 ease-out ripple-effect hover-scale bubble-btn ${leftCollapsed ? 'justify-center p-2.5' : 'justify-start px-3 py-2.5 gap-3 text-sm'}`}
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? <Sun className={`animate-subtle-float ${leftCollapsed ? 'w-5 h-5' : 'w-5 h-5'}`} /> : <Moon className={`animate-subtle-float ${leftCollapsed ? 'w-5 h-5' : 'w-5 h-5'}`} />}
            {!leftCollapsed && <span className="whitespace-nowrap overflow-hidden transition-all duration-300">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
          </button>
        {(() => {
          const currentGuild = guilds.find((g) => g.id === guildId);
          if (!currentGuild?.canManage) return null;
          const active = activeTab === 'settings';
          const iconSizeClass = leftCollapsed ? 'w-6 h-6' : 'w-5 h-5';
          return (
            <div className="pb-1">
              <button
                className={`w-full rounded-2xl flex items-center transition-all duration-300 ease-out ripple-effect hover-scale ${
                  active ? 'btn-gradient animate-glow-pulse' : 'bubble-btn'
                } ${leftCollapsed ? 'justify-center p-2.5' : 'justify-start px-3 py-2.5 gap-3 text-sm'}`}
                onClick={(event) => {
                  selectTab('settings', event.currentTarget);
                  if (guildId) loadGuildSettings(guildId);
                }}
                title="Settings"
              >
                <Settings className={`animate-subtle-float ${iconSizeClass}`} strokeWidth={leftCollapsed ? 2.5 : 2.0} />
                {!leftCollapsed && <span className="whitespace-nowrap overflow-hidden transition-all duration-300">Settings</span>}
              </button>
            </div>
          );
        })()}
        </div>
      </aside>

      {/* Right sidebar toggle - always visible at vertical center of right area */}
      <button
        className={`hidden lg:flex fixed z-40 w-4 h-8 rounded-l-lg bg-white/70 dark:bg-purple-950/70 border border-r-0 border-white/30 dark:border-purple-700/30 items-center justify-center hover:bg-white/90 dark:hover:bg-purple-900/70 transition-all`}
        style={{ top: '50%', right: rightDocked ? 0 : `${LAYOUT.rightOffset + LAYOUT.rightWidth}px`, transform: 'translateY(-50%)', transition: 'right 0.3s ease' }}
        onClick={() => setRightDocked(!rightDocked)}
      >
        {rightDocked ? <ChevronLeft className="w-2.5 h-2.5 text-gray-500 dark:text-gray-400" /> : <ChevronRight className="w-2.5 h-2.5 text-gray-500 dark:text-gray-400" />}
      </button>

      {/* === RIGHT SIDEBAR === */}
      <aside
        className={`hidden lg:flex fixed top-5 bottom-28 z-30 y2k-card rounded-3xl p-3 flex-col overflow-visible transition-all duration-300 ease-out ${
          rightDocked ? 'pointer-events-none opacity-0' : 'opacity-100'
        }`}
        style={{ ...rightSidebarStyle, transform: rightDocked ? 'translateX(calc(100% + 20px))' : 'translateX(0)' }}
      >
        <div className="mb-1" />

        <section className="rounded-2xl bubble-btn p-3 h-[58%] overflow-auto">
          <div className="text-lg font-extrabold text-gray-800 dark:text-white mb-2 leading-tight">
            Queue <span className="text-xs font-medium text-gray-500 dark:text-gray-400">({queue.length})</span>
          </div>
          {guildLoading ? (
            <div className="space-y-2">
              <SkeletonBlock className="h-12" />
              <SkeletonBlock className="h-12" />
              <SkeletonBlock className="h-12" />
            </div>
          ) : (
            <div className="space-y-1">
              {queue.map((item, index) => (
                <div
                  key={item.id || `${item.title}-${index}`}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragIndex(index); }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverIndex(index); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex !== null && dragIndex !== index) {
                      control('/queue/reorder', { from: dragIndex, to: index });
                    }
                    setDragIndex(null);
                    setDragOverIndex(null);
                  }}
                  onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                  onTouchStart={(e) => {
                    const touch = e.touches[0];
                    dragStartY.current = touch.clientY;
                    dragActive.current = false;
                    setDragIndex(index);
                  }}
                  onTouchMove={(e) => {
                    if (dragIndex === null) return;
                    dragActive.current = true;
                    const touch = e.touches[0];
                    const target = document.elementFromPoint(touch.clientX, touch.clientY);
                    const itemEl = target?.closest('[data-queue-index]');
                    if (itemEl) {
                      const overIdx = Number(itemEl.getAttribute('data-queue-index'));
                      setDragOverIndex(overIdx);
                    }
                  }}
                  onTouchEnd={() => {
                    if (dragActive.current && dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
                      control('/queue/reorder', { from: dragIndex, to: dragOverIndex });
                    }
                    setDragIndex(null);
                    setDragOverIndex(null);
                    dragActive.current = false;
                  }}
                  data-queue-index={index}
                  className={`rounded-xl bg-white/30 dark:bg-white/5 p-2 cursor-grab active:cursor-grabbing flex items-center gap-2 transition-all select-none ${
                    dragOverIndex === index && dragIndex !== null && dragIndex !== index ? 'ring-2 ring-fuchsia-400/50 scale-[1.02]' : ''
                  } ${dragIndex === index ? 'opacity-40 scale-95' : ''}`}
                >
                  <span className="text-[10px] text-gray-400 w-4 text-center flex-shrink-0 pointer-events-none">{index + 1}</span>
                  <div className="flex-1 min-w-0 pointer-events-none">
                    <div className="text-xs font-semibold text-gray-800 dark:text-white truncate">{item.title}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{sourceLabel(item.source)}</div>
                  </div>
                  <button className="text-[10px] text-red-400 hover:text-red-500 p-1 flex-shrink-0" onClick={(e) => { e.stopPropagation(); control('/queue/remove', { index }); }}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {!queue.length && <div className="text-xs text-gray-500 dark:text-gray-400">Queue is empty.</div>}
            </div>
          )}
        </section>

        <section className="rounded-2xl bubble-btn p-3 h-[42%] overflow-auto mt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-lg font-extrabold text-gray-800 dark:text-white leading-tight">Voice Members</div>
            <button 
              onClick={() => guildId && refreshVoiceMembers(guildId)}
              className="text-xs px-2 py-1 rounded-lg bg-white/30 hover:bg-white/30 transition-colors"
              title="Refresh"
            >
              ↻
            </button>
          </div>
          {guildLoading ? (
            <div className="space-y-2">
              <SkeletonBlock className="h-10" />
              <SkeletonBlock className="h-10" />
            </div>
          ) : (
            <div className="space-y-2">
              {(voice?.members || []).map((member) => (
                <div key={member.userId} className="rounded-xl bg-white/30 dark:bg-white/5 p-2 flex items-center gap-2">
                  {member.avatarUrl ? (
                    <img src={member.avatarUrl} alt={member.username} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${member.bot ? 'bg-purple-500' : 'bg-gradient-to-br from-pink-400 to-cyan-400'}`}>
                      {member.username.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-gray-800 dark:text-white truncate">
                      {member.bot ? `🤖 ${member.username}` : member.username}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                      {member.muted ? '🔇 muted' : member.deafened ? '🔇 deafened' : '🔊 live'}
                    </div>
                  </div>
                </div>
              ))}
              {!(voice?.members || []).length && (
                <div className="text-center py-4">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">No members in voice</div>
                  <div className="text-[10px] text-gray-400">Join a voice channel and refresh</div>
                </div>
              )}
            </div>
          )}
        </section>
      </aside>

      {/* === MAIN CONTENT === */}
      <main className="layout-main relative z-10 px-4 pt-5 pb-0 transition-all overflow-hidden flex flex-col" style={{ ...mainLayoutStyle, position: 'fixed', top: 0, bottom: '112px', left: 0, right: 0 }}>
        <div className="mb-3 flex items-center justify-between gap-3 tkt-logo relative z-50 flex-shrink-0">
          <div className="bubble-btn rounded-2xl px-3 py-2 text-sm font-semibold flex items-center gap-2">
  <img src="/logo.png" alt="TKT" className="w-5 h-5 rounded-md" />
  <span className="gradient-text-animated">TKT Music</span>
</div>
          {guildName && (
            <div className="relative" ref={serverPickerRef}>
              <button
                onClick={() => setServerPickerOpen(!serverPickerOpen)}
                className="bubble-btn rounded-2xl px-3 py-2 text-xs text-gray-700 dark:text-gray-200 inline-flex items-center gap-1.5 max-w-[220px] transition-all hover:bg-white/80 dark:hover:bg-purple-900/60"
              >
                {activeGuildIcon ? (
                  <img src={activeGuildIcon} alt={guildName} className="w-3.5 h-3.5 rounded-sm object-cover" />
                ) : (
                  <span className="w-3.5 h-3.5 rounded-sm chrome-gradient inline-flex items-center justify-center text-[9px] font-bold text-gray-700">
                    {guildName.slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="truncate">{guildName}</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${serverPickerOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Animated Server Picker Dropdown */}
              <div
                ref={serverPickerPanelRef}
                className={`absolute right-0 top-full mt-2 z-50 w-72 transition-all duration-300 ease-out ${serverPickerOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}`}
              >
                <div className="y2k-card rounded-2xl p-3 shadow-2xl">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Switch Server</span>
                    <button
                      onClick={() => setServerPickerOpen(false)}
                      className="w-6 h-6 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/50 dark:hover:bg-white/10 transition-all"
                    >
                      <ChevronDown className="w-3.5 h-3.5 rotate-180" />
                    </button>
                  </div>
                  <div className="max-h-64 overflow-auto ui-scroll space-y-1">
                    {guilds.filter((g) => guildPresence[g.id]?.joined === true).map((guild) => {
                      const isSelected = guild.id === guildId;
                      const iconUrl = guild.icon
                        ? `https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png?size=64`
                        : null;
                      const botJoined = guildPresence[guild.id]?.joined;
                      const hasAccess = guild.canManage || guild.canDJ;

                      return (
                        <button
                          key={guild.id}
                          onClick={() => {
                            if (hasAccess) {
                              persistGuild(guild.id, guild.name);
                              setServerPickerOpen(false);
                            }
                          }}
                          disabled={!hasAccess}
                          data-picker-item
                          className={`w-full text-left rounded-xl px-3 py-2.5 transition-all ${
                            isSelected
                              ? 'btn-gradient'
                              : hasAccess
                              ? 'bubble-btn hover:bg-white/80 dark:hover:bg-purple-900/50'
                              : 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            {iconUrl ? (
                              <img src={iconUrl} alt={guild.name} className="w-8 h-8 rounded-lg object-cover border border-white/30" />
                            ) : (
                              <div className="w-8 h-8 rounded-lg chrome-gradient flex items-center justify-center text-xs font-bold text-gray-700">
                                {guild.name.slice(0, 1).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">{guild.name}</div>
                              <div className="flex items-center gap-2 text-[10px] opacity-80">
                                {botJoined === true && <span className="text-green-500">● Bot Active</span>}
                                {botJoined === false && <span className="text-orange-500">○ Bot Offline</span>}
                                {!guild.canManage && guild.canDJ && <span className="text-fuchsia-400">DJ</span>}
                                {!hasAccess && <span className="text-red-400">No Permission</span>}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 pt-2 border-t border-white/20 dark:border-purple-700/30 space-y-1">
                    {botInviteUrl && (
                      <a
                        href={botInviteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left px-3 py-2 text-xs text-fuchsia-600 dark:text-fuchsia-400 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-900/20 rounded-xl transition-colors flex items-center gap-2"
                      >
                        <Plus className="w-3 h-3" /> Add Bot to Server
                      </a>
                    )}
                    <button
                      onClick={async () => {
                        localStorage.removeItem('tkt-session-cache');
                        try {
                          const session = await api<SessionPayload>(CONSTANTS.API_PATHS.SESSION);
                          setCsrfToken(session.csrfToken);
                          setGuilds(session.guilds || []);
                          localStorage.setItem('tkt-session-cache', JSON.stringify({ ...session, cachedAt: Date.now() }));
                        } catch { /* ignore */ }
                        setServerPickerOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-xl transition-colors flex items-center gap-2"
                    >
                      <RefreshCw className="w-3 h-3" /> Refresh Servers
                    </button>
                    <button
                      onClick={() => {
                        localStorage.removeItem('tkt-selected-guild-id');
                        localStorage.removeItem('tkt-selected-guild-name');
                        window.location.reload();
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                    >
                      Disconnect Server
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {activeTab === 'home' && (
          <section ref={activeTabPanelRef} data-tab-panel="home" className="flex flex-col gap-3 dashboard-anim tab-panel overflow-hidden flex-1 min-h-0">
            {/* Bot Status Pill + Now Playing */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={async () => {
                  if (!canControl || voiceLoading) return;
                  setVoiceLoading(true);
                  try {
                    await api(voice?.inVoice ? '/player/leave' : '/player/join', {
                      method: 'POST', payload: { guildId }, csrfToken, activityToken
                    });
                    setTimeout(() => { if (guildId) refreshVoiceMembers(guildId); }, 1000);
                  } catch { /* ignore */ } finally { setVoiceLoading(false); }
                }}
                disabled={!canControl || voiceLoading}
                className={`bubble-btn rounded-2xl px-3 py-2 text-xs font-medium flex items-center gap-2 transition-all ripple-effect hover-scale flex-shrink-0 ${
                  voice?.inVoice ? 'text-green-600 dark:text-green-400' : 'text-gray-500'
                }`}
              >
                {voice?.inVoice ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />}
                {voiceLoading ? '...' : voice?.inVoice ? 'Leave' : 'Join'}
              </button>
              <div className={`visualizer-bars flex-shrink-0 ${!player.nowPlaying || player.paused ? 'paused' : ''}`} style={{ opacity: player.nowPlaying ? 1 : 0, transition: 'opacity 0.5s ease' }}>
                <div className="visualizer-bar" /><div className="visualizer-bar" /><div className="visualizer-bar" /><div className="visualizer-bar" /><div className="visualizer-bar" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-lg font-bold text-gray-800 dark:text-white truncate">{player.nowPlaying?.title || 'Nothing Playing'}</div>
                <div className="text-xs text-fuchsia-600 dark:text-fuchsia-400 truncate">{player.nowPlaying?.author || sourceLabel(player.nowPlaying?.source)}</div>
              </div>
            </div>

            {/* Track Reactions */}
            {player.nowPlaying && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {REACTION_EMOJIS.map((emoji) => {
                  const r = reactions[emoji];
                  const hasReacted = r?.users?.includes(currentUser?.id || '');
                  return (
                    <button
                      key={emoji}
                      onClick={async () => {
                        // Floating emoji from now-playing bar
                        const bar = document.querySelector('nav.fixed');
                        if (bar) {
                          const barRect = bar.getBoundingClientRect();
                          const randomX = barRect.left + Math.random() * barRect.width * 0.6 + barRect.width * 0.2;
                          const el = document.createElement('div');
                          el.className = 'emoji-float';
                          el.textContent = emoji;
                          el.style.left = `${randomX - 16}px`;
                          el.style.bottom = `${window.innerHeight - barRect.top + 8}px`;
                          document.body.appendChild(el);
                          setTimeout(() => el.remove(), 2000);
                        }
                        try {
                          const res = await api<{ reactions: typeof reactions }>('/player/react', {
                            method: 'POST', payload: { guildId, emoji }, csrfToken, activityToken
                          });
                          setReactions(res.reactions || {});
                        } catch { /* ignore */ }
                      }}
                      className={`rounded-xl px-2.5 py-1 text-sm transition-all hover-scale ${
                        hasReacted
                          ? 'bg-fuchsia-500/20 ring-1 ring-fuchsia-400/50'
                          : 'bubble-btn'
                      }`}
                    >
                      {emoji}{r && r.count > 0 && <span className="ml-1 text-xs font-semibold">{r.count}</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Audio Effects */}
            {player.nowPlaying && (
              <div className="flex items-center gap-1.5 flex-shrink-0 overflow-x-auto pb-1">
                {EFFECT_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={async () => {
                      const newPreset = activePreset === preset.id ? null : preset.id;
                      try {
                        await api('/player/filters', {
                          method: 'POST',
                          payload: { guildId, preset: newPreset },
                          csrfToken, activityToken
                        });
                        setActivePreset(newPreset);
                      } catch { /* ignore */ }
                    }}
                    disabled={!canControl}
                    className={`rounded-xl px-2.5 py-1 text-[11px] font-medium whitespace-nowrap transition-all hover-scale ${
                      activePreset === preset.id
                        ? 'btn-gradient animate-glow-pulse'
                        : 'bubble-btn'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}

            {/* Quick Play URL */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="relative flex-1">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  className="w-full appearance-none rounded-2xl bubble-btn pl-9 pr-4 py-2 text-sm text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:ring-2 focus:ring-fuchsia-400/50 transition-all"
                  placeholder="Paste a YouTube, Spotify, or SoundCloud link..."
                  value={quickPlayUrl}
                  onChange={(e) => setQuickPlayUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') playUrl(quickPlayUrl); }}
                  disabled={!canControl || quickPlayLoading}
                />
              </div>
              <button
                className="btn-gradient rounded-2xl px-3 py-2 text-sm font-medium transition-all hover-scale ripple-effect disabled:opacity-50 flex items-center gap-1.5"
                disabled={!canControl || !quickPlayUrl.trim() || quickPlayLoading}
                onClick={() => playUrl(quickPlayUrl)}
              >
                <Play className="w-4 h-4" />
                {quickPlayLoading ? '...' : 'Play'}
              </button>
            </div>

            {/* Queue/History Tabbed Component */}
            <article className="y2k-card rounded-3xl p-4 dashboard-anim flex flex-col flex-1 min-h-0">
              {/* Tabs */}
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => setDashboardQueueTab('queue')}
                  className={`flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    dashboardQueueTab === 'queue'
                      ? 'btn-gradient'
                      : 'bubble-btn hover-lift'
                  }`}
                >
                  <ListMusic className="w-4 h-4" />
                  Queue ({queue.length}){queue.length > 0 && <span className="opacity-60">· ~{Math.round(queue.length * 3.5)}m</span>}
                </button>
                {dashboardQueueTab === 'queue' && queue.length > 0 && (
                  <>
                    <button
                      onClick={() => control('/queue/shuffle')}
                      disabled={!canControl}
                      className="bubble-btn rounded-xl p-2 hover-scale ripple-effect"
                      title="Shuffle Queue"
                    >
                      <Shuffle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => control('/player/stop')}
                      disabled={!canControl}
                      className="bubble-btn rounded-xl p-2 hover-scale ripple-effect text-red-500"
                      title="Clear Queue"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => setDashboardQueueTab('history')}
                  className={`flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    dashboardQueueTab === 'history'
                      ? 'btn-gradient'
                      : 'bubble-btn hover-lift'
                  }`}
                >
                  <Clock className="w-4 h-4" />
                  History ({history.length})
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto ui-scroll space-y-2">
                {dashboardQueueTab === 'queue' ? (
                  queue.length > 0 ? (
                    queue.map((item, index) => (
                      <div
                        key={item.id || `${item.title}-${index}`}
                        className="rounded-xl bubble-btn p-2.5 hover-lift flex items-center gap-2"
                      >
                        <span className="text-xs text-gray-400 w-5">{index + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 dark:text-white truncate">
                            {item.title}
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {sourceLabel(item.source)}
                          </div>
                        </div>
                        {canControl && (
                          <button
                            onClick={() => control('/queue/remove', { index })}
                            className="p-1 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors"
                            title="Remove from queue"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                      <ListMusic className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <div className="text-sm">Queue is empty</div>
                      <div className="text-xs mt-1">Add songs from the Search tab</div>
                    </div>
                  )
                ) : (
                  history.length > 0 ? (
                    history.map((item, index) => (
                      <div
                        key={item.id || `${item.title}-${index}`}
                        className="rounded-xl bubble-btn p-2.5 hover-lift flex items-center gap-2"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 dark:text-white truncate">
                            {item.title}
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {sourceLabel(item.source)} · {new Date(item.playedAt).toLocaleDateString()}
                            {(item.playCount ?? 1) > 1 && (
                              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400 font-semibold">
                                {item.playCount}x
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          className="p-1.5 rounded-lg bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/20 transition-all hover:scale-110 flex-shrink-0"
                          onClick={() => queueTrack({ id: item.id, title: item.title, url: item.url, source: item.source })}
                          title="Play again"
                        >
                          <Play className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                      <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <div className="text-sm">No history yet</div>
                      <div className="text-xs mt-1">Played tracks will appear here</div>
                    </div>
                  )
                )}
              </div>
            </article>
          </section>
        )}

        {activeTab === 'search' && (
          <section ref={activeTabPanelRef} data-tab-panel="search" className="dashboard-anim tab-panel flex-1 min-h-0">
            <SearchResultsCard
              searchQuery={searchQuery}
              isLoading={loading}
              results={searchResults}
              onSearchInputChange={setSearchQuery}
              onSearchSubmit={searchTracks}
              onQueueTrack={queueTrack}
              onPlayNowTrack={playNow}
              onFavoriteTrack={addFavorite}
              onTrackAddToPlaylist={addToPlaylist}
              searchSource={searchSource}
              onSearchSourceChange={setSearchSource}
            />
          </section>
        )}

        {activeTab === 'playlists' && (
          <section
            ref={activeTabPanelRef}
            data-tab-panel="playlists"
            className="y2k-card rounded-3xl p-4 sm:p-5 dashboard-anim tab-panel flex flex-col flex-1 min-h-0"
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4 flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                <FolderHeart className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-base font-bold text-gray-800 dark:text-white">Playlists</div>
                <div className="text-xs text-gray-500">{library.playlists.length} playlists</div>
              </div>
            </div>

            {/* Create New */}
            <div className="flex gap-2 mb-4 flex-shrink-0">
              <input
                className="flex-1 h-10 rounded-xl bg-white/40 dark:bg-purple-950/50 border border-white/40 dark:border-purple-700/30 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/60"
                value={playlistName}
                onChange={(event) => setPlaylistName(event.target.value)}
                placeholder="New playlist name..."
                onKeyDown={(e) => e.key === 'Enter' && createPlaylist()}
              />
              <button 
                className="h-10 px-4 btn-gradient rounded-xl flex items-center gap-2" 
                onClick={createPlaylist}
                disabled={!playlistName.trim()}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Playlist Tabs */}
            {library.playlists.length > 0 && (
              <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1 flex-shrink-0">
                {library.playlists.map((playlist) => (
                  <button
                    key={playlist.id}
                    onClick={() => { setSelectedPlaylistId(playlist.id); setShowSharePanel(false); }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                      selectedPlaylistId === playlist.id
                        ? 'btn-gradient'
                        : 'bubble-btn hover-lift'
                    }`}
                  >
                    {playlist.shared && <Users className="w-3 h-3 opacity-70" />}
                    {playlist.name}
                    <span className="text-xs opacity-60">({playlist.items.length})</span>
                  </button>
                ))}
                {selectedPlaylistId && selectedPlaylist && !selectedPlaylist.shared && (
                  <button
                    onClick={() => { setShowSharePanel(!showSharePanel); if (!showSharePanel) { loadCollaborators(selectedPlaylistId); loadServerMembers(); } }}
                    className={`p-2 rounded-xl transition-all flex-shrink-0 ${showSharePanel ? 'btn-gradient' : 'bubble-btn hover-lift'}`}
                    title="Share playlist"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            {/* Share Panel */}
            {showSharePanel && selectedPlaylistId && (
              <div className="mb-4 rounded-2xl bg-white/30 dark:bg-white/5 p-3 space-y-3">
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Share2 className="w-3.5 h-3.5" /> Share "{selectedPlaylist?.name}"
                </div>
                {/* Server members list */}
                <div className="max-h-40 overflow-auto ui-scroll space-y-1">
                  {serverMembers.filter((m) => m.userId !== currentUser?.id).map((member) => {
                    const alreadyShared = collaborators.some((c) => c.userId === member.userId);
                    return (
                      <div key={member.userId} className="flex items-center justify-between rounded-xl bg-white/20 dark:bg-white/5 px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {member.avatarUrl ? (
                            <img src={member.avatarUrl} alt="" className="w-5 h-5 rounded-full flex-shrink-0" />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-pink-400 to-cyan-400 flex-shrink-0" />
                          )}
                          <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{member.displayName}</span>
                        </div>
                        {alreadyShared ? (
                          <button
                            onClick={() => unsharePlaylist(member.userId)}
                            className="text-[10px] px-2 py-1 rounded-lg text-red-400 hover:text-red-500 flex-shrink-0"
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            onClick={() => sharePlaylist(member.userId)}
                            disabled={shareLoading}
                            className="text-[10px] px-2 py-1 rounded-lg btn-gradient disabled:opacity-40 flex-shrink-0"
                          >
                            Invite
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {serverMembers.length === 0 && (
                    <div className="text-xs text-gray-500 py-2">Loading members...</div>
                  )}
                </div>
              </div>
            )}

            {/* Import from YouTube/Spotify/SoundCloud */}
            <div className="mb-4 flex-shrink-0">
              <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Import from YouTube, Spotify, or SoundCloud</div>
              <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      className="w-full appearance-none rounded-xl bubble-btn pl-9 pr-3 h-10 text-sm text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:ring-2 focus:ring-cyan-400/50 transition-all"
                      placeholder={selectedPlaylistId ? 'Paste a YouTube, Spotify, or SoundCloud playlist URL...' : 'Select a playlist above first...'}
                      value={importUrl}
                      onChange={(e) => { setImportUrl(e.target.value); setImportResult(null); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') importPlaylist(); }}
                      disabled={importLoading}
                    />
                  </div>
                  <button
                    className="h-10 px-3 btn-gradient rounded-xl flex items-center gap-1.5 text-sm font-medium disabled:opacity-50"
                    disabled={!importUrl.trim() || importLoading || !selectedPlaylistId}
                    onClick={importPlaylist}
                  >
                    <Download className="w-4 h-4" />
                    {importLoading ? 'Importing...' : 'Import'}
                  </button>
                </div>
                {importLoading && importProgress && (
                  <div className="mt-2 text-xs text-fuchsia-500 dark:text-fuchsia-400 animate-pulse">{importProgress}</div>
                )}
                {importResult && (
                  <div className={`mt-2 rounded-xl px-3 py-2 text-xs font-medium flex items-center gap-2 ${
                    importResult.includes('Imported')
                      ? 'bg-lime-500/10 text-lime-600 dark:text-lime-400'
                      : 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                  }`}>
                    {importResult.includes('Imported') ? <Music className="w-3.5 h-3.5 flex-shrink-0" /> : null}
                    {importResult}
                  </div>
                )}
              </div>

            {/* Tracks */}
            <div className="space-y-2 flex-1 min-h-0 overflow-auto ui-scroll">
              {selectedPlaylist ? (
                selectedPlaylist.items.length > 0 ? (
                  selectedPlaylist.items.map((item, index) => (
                    <div key={`${item.id}-${index}`} className="rounded-xl bubble-btn p-3 hover-lift flex items-center gap-3 group">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400/20 to-blue-500/20 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 dark:text-white truncate">{item.title}</div>
                        <div className="text-[10px] text-gray-500 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500" />
                          {sourceLabel(item.source)}
                        </div>
                      </div>
                      <button
                        className="p-2 rounded-lg bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/20 transition-all hover:scale-110"
                        onClick={() => queueTrack(item)}
                        title="Add to Queue"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Music className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <div className="text-sm">Empty playlist</div>
                    <div className="text-xs mt-1">Add songs from search</div>
                  </div>
                )
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <FolderHeart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <div className="text-sm">Select a playlist</div>
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'favorites' && (
          <section
            ref={activeTabPanelRef}
            data-tab-panel="favorites"
            className="y2k-card rounded-3xl p-4 sm:p-5 dashboard-anim tab-panel flex flex-col flex-1 min-h-0"
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4 flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-base font-bold text-gray-800 dark:text-white">Favorites</div>
                <div className="text-xs text-gray-500">{library.favorites.length} songs</div>
              </div>
            </div>

            {/* Favorites List */}
            <div className="space-y-2 flex-1 min-h-0 overflow-auto ui-scroll">
              {library.favorites.length > 0 ? (
                library.favorites.map((item, index) => (
                  <div key={`${item.id}-${index}`} className="rounded-xl bubble-btn p-3 hover-lift flex items-center gap-3 group">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-400/20 to-rose-500/20 flex items-center justify-center flex-shrink-0">
                      <Heart className="w-5 h-5 text-pink-500 fill-pink-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 dark:text-white truncate">{item.title}</div>
                      <div className="text-[10px] text-gray-500 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-pink-400 to-rose-500" />
                        {sourceLabel(item.source)}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button 
                        className="p-2 rounded-lg bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/20 transition-all hover:scale-110"
                        onClick={() => queueTrack(item)}
                        title="Add to Queue"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button 
                        className="p-2 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-all hover:scale-110"
                        onClick={() => removeFavorite(item)}
                        title="Remove from Favorites"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Heart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <div className="text-sm">No favorites yet</div>
                  <div className="text-xs mt-1">Heart songs to add them here</div>
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'stats' && (
          <section ref={activeTabPanelRef} data-tab-panel="stats" className="y2k-card rounded-3xl p-4 sm:p-5 dashboard-anim tab-panel flex flex-col flex-1 min-h-0">
            <div className="flex items-center gap-3 mb-4 flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-indigo-500 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-base font-bold text-gray-800 dark:text-white">Your Stats</div>
                <div className="text-xs text-gray-500">{currentUser?.username || 'User'}</div>
              </div>
              <button onClick={loadStats} className="ml-auto bubble-btn rounded-xl px-3 py-1.5 text-xs hover-scale">
                <RefreshCw className="w-3 h-3" />
              </button>
            </div>

            {!userStats && !statsLoading && (
              <div className="flex-1 flex items-center justify-center">
                <button onClick={loadStats} className="btn-gradient rounded-2xl px-6 py-3 text-sm font-medium hover-scale ripple-effect">
                  Load Your Stats
                </button>
              </div>
            )}

            {statsLoading && (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-pulse flex space-x-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}

            {userStats && !statsLoading && (
              <div className="flex-1 overflow-auto ui-scroll space-y-4">
                {/* Overview cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="rounded-2xl bubble-btn p-3 text-center">
                    <div className="text-xl font-bold text-gray-800 dark:text-white">{userStats.totalPlays}</div>
                    <div className="text-[10px] text-gray-500">Tracks Played</div>
                  </div>
                  <div className="rounded-2xl bubble-btn p-3 text-center">
                    <div className="text-xl font-bold text-gray-800 dark:text-white">{Math.round(userStats.totalTimeMs / 60000)}</div>
                    <div className="text-[10px] text-gray-500">Minutes Listened</div>
                  </div>
                  <div className="rounded-2xl bubble-btn p-3 text-center">
                    <div className="text-xl font-bold text-gray-800 dark:text-white">{userStats.totalSearches}</div>
                    <div className="text-[10px] text-gray-500">Searches</div>
                  </div>
                  <div className="rounded-2xl bubble-btn p-3 text-center">
                    <div className="text-xl font-bold text-gray-800 dark:text-white">{userStats.totalSkips}</div>
                    <div className="text-[10px] text-gray-500">Skips</div>
                  </div>
                </div>

                {userStats.firstSeen && (
                  <div className="text-xs text-gray-500 text-center">
                    Listening since {new Date(userStats.firstSeen).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </div>
                )}

                {/* Top Tracks */}
                {userStats.topTracks.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Top Tracks</div>
                    <div className="space-y-1.5">
                      {userStats.topTracks.map((track, i) => (
                        <div key={i} className="flex items-center gap-2 rounded-xl bubble-btn p-2">
                          <span className="text-xs font-bold text-gray-400 w-5 text-center">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-gray-800 dark:text-white truncate">{track.title}</div>
                            {track.author && <div className="text-[10px] text-gray-500 truncate">{track.author}</div>}
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <div className="h-1.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-400" style={{ width: `${Math.max(16, (track.plays / userStats.topTracks[0].plays) * 60)}px` }} />
                            <span className="text-[10px] font-semibold text-gray-500">{track.plays}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Artists */}
                {userStats.topArtists.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Top Artists</div>
                    <div className="space-y-1.5">
                      {userStats.topArtists.map((artist, i) => (
                        <div key={i} className="flex items-center gap-2 rounded-xl bubble-btn p-2">
                          <span className="text-xs font-bold text-gray-400 w-5 text-center">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-gray-800 dark:text-white truncate">{artist.author}</div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <div className="h-1.5 rounded-full bg-gradient-to-r from-pink-500 to-violet-400" style={{ width: `${Math.max(16, (artist.plays / userStats.topArtists[0].plays) * 60)}px` }} />
                            <span className="text-[10px] font-semibold text-gray-500">{artist.plays}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {userStats.totalPlays === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <div className="text-sm">No stats yet</div>
                    <div className="text-xs mt-1">Play some music and your stats will appear here</div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {activeTab === 'settings' && (
          <section ref={activeTabPanelRef} data-tab-panel="settings" className="y2k-card rounded-3xl p-4 sm:p-5 dashboard-anim tab-panel flex-1 min-h-0 overflow-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-base font-bold text-gray-800 dark:text-white">Server Settings</div>
                <div className="text-xs text-gray-500">Configure bot behavior for this server</div>
              </div>
            </div>

            {settingsLoading && !guildRoles.length ? (
              <div className="space-y-2 py-2">
                <SkeletonBlock className="h-10 w-full" />
                <SkeletonBlock className="h-10 w-full" />
                <SkeletonBlock className="h-10 w-1/3" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* DJ Role */}
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">DJ Role</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Users with this role can control the bot and see this server on the dashboard.</div>
                  <div className="relative">
                    <select
                      className="w-full appearance-none rounded-2xl bg-white/60 dark:bg-purple-950/60 border border-white/40 dark:border-purple-700/30 px-4 py-3 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-fuchsia-400/50 transition-all cursor-pointer pr-10 [&>option]:bg-white [&>option]:dark:bg-gray-900 [&>option]:text-gray-800 [&>option]:dark:text-white"
                      value={selectedDjRoleId || ''}
                      onChange={(e) => { setSelectedDjRoleId(e.target.value || null); setSettingsSaved(false); }}
                    >
                      <option value="">No DJ role (anyone can use commands)</option>
                      {guildRoles.map((role) => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Now Playing Channel */}
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">Now Playing Announcements</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Send a message to a text channel whenever a new track starts playing.</div>
                  <div className="relative">
                    <select
                      className="w-full appearance-none rounded-2xl bg-white/60 dark:bg-purple-950/60 border border-white/40 dark:border-purple-700/30 px-4 py-3 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-fuchsia-400/50 transition-all cursor-pointer pr-10 [&>option]:bg-white [&>option]:dark:bg-gray-900 [&>option]:text-gray-800 [&>option]:dark:text-white"
                      value={selectedNowPlayingChannelId || ''}
                      onChange={(e) => { setSelectedNowPlayingChannelId(e.target.value || null); setSettingsSaved(false); }}
                    >
                      <option value="">Disabled</option>
                      {guildChannels.map((ch) => (
                        <option key={ch.id} value={ch.id}>#{ch.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Vote to Skip */}
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">Vote to Skip</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Require multiple people to vote before a track is skipped.</div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => { setVoteSkipEnabled(!voteSkipEnabled); setSettingsSaved(false); }}
                      className={`relative w-11 h-6 rounded-full transition-all ${voteSkipEnabled ? 'bg-fuchsia-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${voteSkipEnabled ? 'left-[22px]' : 'left-0.5'}`} />
                    </button>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{voteSkipEnabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  {voteSkipEnabled && (
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-gray-500">Threshold:</span>
                      <input
                        type="range"
                        min="25"
                        max="100"
                        step="5"
                        value={voteSkipThreshold}
                        onChange={(e) => { setVoteSkipThreshold(Number(e.target.value)); setSettingsSaved(false); }}
                        className="flex-1 accent-fuchsia-500"
                      />
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-10 text-right">{voteSkipThreshold}%</span>
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <div className="flex items-center gap-3">
                  <button
                    className="btn-gradient rounded-xl px-4 py-2 text-sm font-medium transition-all hover-scale ripple-effect disabled:opacity-50"
                    disabled={settingsLoading}
                    onClick={saveSettings}
                  >
                    {settingsLoading ? 'Saving...' : 'Save'}
                  </button>
                  {settingsSaved && (
                    <span className="text-xs text-lime-500 font-medium animate-pulse">Saved!</span>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === 'lyrics' && (
          <section ref={activeTabPanelRef} data-tab-panel="lyrics" className="flex flex-col dashboard-anim tab-panel flex-1 min-h-0">
            <article className="y2k-card rounded-3xl p-5 flex flex-col flex-1 min-h-0">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4 flex-shrink-0">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                  <Type className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-bold text-gray-800 dark:text-white truncate">{player.nowPlaying?.title || 'No Track'}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{player.nowPlaying?.author || sourceLabel(player.nowPlaying?.source)}</div>
                </div>
              </div>

              {/* Lyrics Content */}
              <div ref={lyricsContainerRef} className="flex-1 overflow-auto ui-scroll">
                {lyricsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-pulse flex space-x-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                ) : lyricsSynced && lyricsLines.length > 0 ? (
                  <div className="space-y-2 py-4">
                    {lyricsLines.map((line, i) => {
                      const isActive = lyricsLines[i + 1]
                        ? displayPosition >= line.startMs && displayPosition < lyricsLines[i + 1].startMs
                        : displayPosition >= line.startMs;
                      return (
                        <div
                          key={i}
                          data-lyrics-line={i}
                          className={`transition-all duration-300 px-2 py-1 rounded-xl ${
                            isActive
                              ? 'text-xl font-bold text-gray-900 dark:text-white bg-white/20 dark:bg-white/10 scale-[1.02]'
                              : 'text-lg text-gray-500 dark:text-gray-500'
                          } ${!line.line.trim() ? 'h-4' : ''}`}
                        >
                          {line.line || '\u00A0'}
                        </div>
                      );
                    })}
                  </div>
                ) : lyrics ? (
                  <pre className="text-lg text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-loose py-4">{lyrics}</pre>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                    <Type className="w-16 h-16 mb-4 opacity-20" />
                    <div className="text-lg font-medium">No lyrics available</div>
                    <div className="text-sm mt-1 opacity-70">Lyrics may not be available for this track</div>
                  </div>
                )}
              </div>
            </article>
          </section>
        )}
      </main>

      {/* === CONTROLS === */}
      <nav className="fixed left-3 right-3 bottom-3 lg:left-6 lg:right-6 z-40 y2k-card rounded-3xl px-3 sm:px-4 py-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {player.nowPlaying?.artworkUrl ? (
              <img src={player.nowPlaying.artworkUrl} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-cyan-400 glow-pink flex-shrink-0" />
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-800 dark:text-white truncate">{player.nowPlaying?.title || 'Nothing Playing'}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{player.nowPlaying?.author || sourceLabel(player.nowPlaying?.source)}</div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-3">
            <button
              className="bubble-btn no-jump-hover rounded-2xl p-2 ripple-effect hover-scale magnetic-hover"
              disabled={!canControl || history.length < 2}
              onClick={() => {
                const prev = history[1];
                if (prev?.url || prev?.title) {
                  queueTrack({ id: prev.id, title: prev.title, url: prev.url, source: prev.source });
                }
              }}
              title="Previous"
            >
              <SkipBack className="w-4 h-4" />
            </button>
            <button className="btn-gradient btn-gradient-clean no-jump-hover rounded-2xl p-3 ripple-effect hover-scale animate-glow-pulse" disabled={!canControl} onClick={() => control(player.paused ? '/player/resume' : '/player/pause')}>
              {player.paused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
            </button>
            <button className="bubble-btn no-jump-hover rounded-2xl p-2 ripple-effect hover-scale magnetic-hover" disabled={!canControl} onClick={() => control('/player/skip')}>
              <SkipForward className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center justify-start sm:justify-end gap-2">
            <div className="hidden md:flex items-center gap-2 bubble-btn rounded-2xl px-2.5 py-2 min-w-[150px]">
              <button
                type="button"
                onClick={() => onVolumeInput(localVolume > 0 ? 0 : 70)}
                disabled={!canControl}
                className="text-gray-600 dark:text-gray-300 disabled:opacity-40"
                title={localVolume > 0 ? 'Mute' : 'Unmute'}
              >
                {localVolume > 0 ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <input
                type="range"
                min="0"
                max="200"
                value={localVolume}
                disabled={!canControl}
                onChange={(event) => onVolumeInput(Number(event.target.value))}
                className="w-20 accent-fuchsia-500 disabled:opacity-40"
                title={`Volume ${localVolume}%`}
              />
              <span className="w-7 text-right text-[10px] tabular-nums text-gray-500 dark:text-gray-400">{localVolume}</span>
            </div>

            {/* Loop Mode Toggle (admin/DJ only) */}
            {(() => {
              const currentGuild = guilds.find((g) => g.id === guildId);
              if (!currentGuild?.canManage) return null;
              return (
                <button
                  onClick={() => {
                    const modes = ['off', 'track', 'queue'];
                    const currentIndex = modes.indexOf(player.loopMode);
                    const nextMode = modes[(currentIndex + 1) % modes.length];
                    control('/player/loop', { mode: nextMode });
                  }}
                  disabled={!canControl}
                  className={`bubble-btn no-jump-hover rounded-2xl p-2 ripple-effect hover-scale transition-all ${
                    player.loopMode !== 'off' ? 'btn-gradient animate-glow-pulse' : ''
                  }`}
                  title={`Loop: ${player.loopMode}`}
                >
                  {player.loopMode === 'track' ? (
                    <Repeat1 className="w-4 h-4" />
                  ) : (
                    <Repeat className={`w-4 h-4 ${player.loopMode === 'queue' ? 'animate-subtle-float' : ''}`} />
                  )}
                </button>
              );
            })()}

            {/* Lyrics Page */}
            <button
              onClick={() => { selectTab(activeTab === 'lyrics' ? 'home' : 'lyrics', null); }}
              className={`bubble-btn no-jump-hover rounded-2xl p-2 ripple-effect hover-scale transition-all ${
                activeTab === 'lyrics' ? 'btn-gradient animate-glow-pulse' : ''
              }`}
              title="Lyrics"
            >
              <Type className="w-4 h-4" />
            </button>

            {/* Connection Status */}
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                status === 'Live'
                  ? 'bg-green-500/20 text-green-500 animate-glow-pulse'
                  : status === 'Reconnecting'
                  ? 'bg-orange-500/20 text-orange-500 animate-pulse-scale'
                  : 'bg-red-500/20 text-red-500'
              }`}
              title={`Status: ${status}`}
            >
              {status === 'Live' ? (
                <Wifi className="w-4 h-4" />
              ) : (
                <WifiOff className="w-4 h-4" />
              )}
            </div>
          </div>
        </div>
        {/* Progress bar (seekable) */}
        {player.nowPlaying && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] tabular-nums text-gray-500 dark:text-gray-400 w-8 text-right">{formatTime(displayPosition)}</span>
            <div
              className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden cursor-pointer group hover:h-3 transition-all duration-200"
              onClick={(e) => {
                if (!canControl || !player.nowPlaying?.durationMs) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                const positionMs = Math.round(pct * player.nowPlaying.durationMs);
                setDisplayPosition(positionMs);
                control('/player/seek', { positionMs });
              }}
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-pink-500 to-cyan-400 transition-all duration-1000 ease-linear group-hover:from-pink-400 group-hover:to-cyan-300"
                style={{ width: `${player.nowPlaying.durationMs ? Math.min(100, (displayPosition / player.nowPlaying.durationMs) * 100) : 0}%` }}
              />
            </div>
            <span className="text-[10px] tabular-nums text-gray-500 dark:text-gray-400 w-8">{formatTime(player.nowPlaying.durationMs || 0)}</span>
          </div>
        )}
      </nav>

      {/* Lyrics popup removed — now a full tab page */}

      {/* === MOBILE TAB BAR === */}
      <nav className="lg:hidden fixed left-3 right-3 bottom-[88px] z-30 y2k-card rounded-2xl px-2 py-1.5 flex items-center justify-around">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              onClick={(e) => selectTab(tab.id, e.currentTarget)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${
                active ? 'text-fuchsia-500' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} />
              <span className="text-[9px] font-medium">{tab.label}</span>
            </button>
          );
        })}
        <button
          onClick={toggleTheme}
          className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all text-gray-500 dark:text-gray-400"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          <span className="text-[9px] font-medium">{theme === 'dark' ? 'Light' : 'Dark'}</span>
        </button>
        {activeGuild?.canManage && (
          <button
            onClick={(e) => {
              selectTab('settings', e.currentTarget);
              if (guildId) loadGuildSettings(guildId);
            }}
            className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${
              activeTab === 'settings' ? 'text-fuchsia-500' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            <Settings className="w-5 h-5" strokeWidth={activeTab === 'settings' ? 2.5 : 2} />
            <span className="text-[9px] font-medium">Settings</span>
          </button>
        )}
      </nav>

      {isActivityMode && activityError && (
        <div className="fixed inset-0 z-50 backdrop-blur-md bg-black/35 flex items-center justify-center p-4">
          <div className="w-full max-w-md y2k-card rounded-3xl p-5 sm:p-6">
            <div className="text-lg font-bold text-gray-800 dark:text-white mb-1">Discord activity sign-in failed</div>
            <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">{activityError}</div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="h-11 rounded-2xl btn-gradient inline-flex w-full items-center justify-center gap-2 text-sm font-semibold ripple-effect hover-scale"
            >
              <RefreshCw className="w-4 h-4" /> Retry
            </button>
          </div>
        </div>
      )}

      {onboardingRequired && (
        <div ref={onboardingRef} className="fixed inset-0 z-50 backdrop-blur-md bg-black/35 flex items-center justify-center p-4">
          <div className="w-full max-w-md y2k-card rounded-3xl p-5 sm:p-6">
            <div className="onboarding-item text-lg font-bold text-gray-800 dark:text-white mb-1">Welcome to TKT Music</div>
            <div className="onboarding-item text-sm text-gray-600 dark:text-gray-300 mb-4">
              {!isAuthenticated ? 'Login with Discord to continue.' : 'Pick a server to continue.'}
            </div>

            {!isAuthenticated ? (
              <a
                href={loginUrl}
                className="onboarding-item h-11 rounded-2xl btn-gradient inline-flex w-full items-center justify-center gap-2 text-sm font-semibold mb-3 ripple-effect hover-scale"
              >
                <LogIn className="w-4 h-4" /> Login with Discord
              </a>
            ) : (
              <>
                <div className="onboarding-item bubble-btn rounded-2xl p-3 mb-3 flex items-center gap-3">
                  {currentUser?.avatar ? (
                    <img
                      src={`https://cdn.discordapp.com/avatars/${currentUser.id}/${currentUser.avatar}.png?size=64`}
                      alt={currentUser.username}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <UserCircle2 className="w-9 h-9 text-gray-500 dark:text-gray-300" />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-800 dark:text-white truncate">{currentUser?.username || 'Discord user'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">Connected with Discord</div>
                  </div>
                </div>

                <div className="onboarding-item text-xs text-gray-500 dark:text-gray-400 mb-1">Pick Your Server</div>
                <div className="onboarding-item relative mb-2">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    className="h-10 w-full rounded-2xl bg-white/40 dark:bg-purple-950/50 border border-white/40 dark:border-purple-700/30 pl-9 pr-3 text-sm"
                    value={onboardingSearch}
                    onChange={(event) => setOnboardingSearch(event.target.value)}
                    placeholder="Search your servers"
                  />
                </div>

                <div className="max-h-48 overflow-auto ui-scroll space-y-2 mb-3 pl-0.5 pr-2">
                  {filteredGuilds.map((guild) => {
                    const selected = onboardingGuildId === guild.id;
                    const iconUrl = guild.icon
                      ? `https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png?size=64`
                      : null;
                    const botJoined = guildPresence[guild.id]?.joined;
                    return (
                      <button
                        key={guild.id}
                        onClick={() => setOnboardingGuildId(guild.id)}
                        className={`onboarding-item w-full text-left rounded-2xl px-3 py-2.5 transition-all overflow-hidden ripple-effect hover-scale ${
                          selected ? 'btn-gradient animate-glow-pulse' : 'bubble-btn hover-lift'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          {iconUrl ? (
                            <img src={iconUrl} alt={guild.name} className="w-9 h-9 rounded-xl object-cover border border-white/40" />
                          ) : (
                            <div className="w-9 h-9 rounded-xl chrome-gradient flex items-center justify-center text-xs font-bold text-gray-700">
                              {guild.name.slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold truncate">{guild.name}</div>
                            <div className="mt-1 grid grid-cols-2 gap-2 text-[10px] opacity-90">
                              <span className="truncate text-left">{guild.canManage ? 'Admin' : guild.canDJ ? 'DJ' : 'No Access'}</span>
                              <span className="truncate text-right">
                                {botJoined === true ? 'Bot: In' : botJoined === false ? 'Bot: Out' : 'Bot: Chk'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {!filteredGuilds.length && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 bubble-btn rounded-xl px-3 py-2">
                      {guildScanInProgress ? 'Checking your servers...' : 'No servers with the bot found.'}
                    </div>
                  )}
                </div>
                {botInviteUrl && (
                  <a
                    href={botInviteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="onboarding-item block text-center mb-3 px-3 py-2.5 text-xs font-medium text-fuchsia-600 dark:text-fuchsia-400 bubble-btn rounded-2xl hover-lift transition-all"
                  >
                    <Plus className="w-3.5 h-3.5 inline mr-1.5" />Add Bot to a Server
                  </a>
                )}

                <div className="rounded-2xl px-3 py-3 mb-3 border border-cyan-300/60 dark:border-cyan-500/40 bg-gradient-to-r from-cyan-500/15 via-fuchsia-500/10 to-pink-500/15 shadow-[0_0_24px_rgba(34,211,238,0.18)]">
                  <div className="onboarding-item text-[10px] text-cyan-700 dark:text-cyan-300 uppercase tracking-wide">Selected Server</div>
                  <div className="flex items-center gap-2.5 mt-1">
                    {selectedGuild?.icon ? (
                      <img
                        src={`https://cdn.discordapp.com/icons/${selectedGuild.id}/${selectedGuild.icon}.png?size=64`}
                        alt={selectedGuild.name}
                        className="w-9 h-9 rounded-xl object-cover border border-white/40"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-xl chrome-gradient flex items-center justify-center text-xs font-bold text-gray-700">
                        {selectedGuild?.name ? selectedGuild.name.slice(0, 1).toUpperCase() : '?'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                        {selectedGuild?.name || 'None selected'}
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-2 text-[10px] text-gray-600 dark:text-gray-300">
                        <span className="truncate text-left">
                          {selectedGuild?.canManage ? 'Admin' : selectedGuild?.canDJ ? 'DJ' : selectedGuild ? 'No Access' : '-'}
                        </span>
                        <span className="truncate text-right">
                          {selectedGuild
                            ? guildPresence[selectedGuild.id]?.joined
                              ? 'Bot: In'
                              : guildPresence[selectedGuild.id]?.joined === false
                                ? 'Bot: Out'
                                : 'Bot: Chk'
                            : 'Bot: -'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  className="onboarding-item h-11 rounded-2xl btn-gradient w-full text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed ripple-effect hover-scale"
                  onClick={applyGuildSelection}
                  disabled={!canContinueWithGuild}
                >
                  Continue
                </button>
                {selectedGuild && !selectedGuild.canManage && !selectedGuild.canDJ && (
                  <div className="text-[11px] text-orange-600 dark:text-orange-400 mt-2">You need Manage Server permission or the DJ role in this guild.</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
